<template>
    <div>
        <limitless-page-header
            :loading="!initialized && busy_loading"
            title="Kortbeställning"
            :sub-title="(this.customer ? 'Kund: ' + this.customer.CompanyName : '') + (!this.not_found ? (this.id > 0 ? ', Ordernummer: ' + this.id : ' - Ny Kortbeställning') : '')"
            :title-template="(this.id > 0 ? 'Kortbeställning #' + this.id : 'Ny Kortbeställning')"
        >
            <template #header-elements>
                <limitless-page-header-button v-if="card_order && customer" :route="{ name: 'customers.edit', params: { id: customer.CustomerID } }" icon="icon-office">Till kund</limitless-page-header-button>
            </template>
        </limitless-page-header>
        <div class="content" v-if="!initialized && busy_loading">
            <i class="icon-spinner2 spinner"></i>
        </div>
        <div class="content" v-else-if="!not_found && card_order && customer">
            <div v-if="!(customer.SellsBrandSHT && customer.CustomerGroupID_SHT || customer.SellsBrandNPC && customer.CustomerGroupID_NPC)" class="alert alert-warning alert-styled-left">
                <div class="font-weight-semibold">Ingen kundgrupp vald</div>
                <div>Denna kund har ingen aktiv kundgrupp för försäljning, gå in på kundbilden och välj kundgrupp högst upp i formuläret.</div>
            </div>
            <div v-if="(customer.VatNo === null || customer.VatNo.length === 0) && (customer.OrgNo === null || customer.OrgNo.length === 0)" class="alert alert-warning alert-styled-left">
                <div class="font-weight-semibold">Inget Org. nr eller VAT nummer angivet på kund.</div>
                <div>Denna kund har ingen Org. nr eller VAT nummer angivet, fakturering ej möjlig, gå in på kundbilden för att ange.</div>
            </div>
            <div class="row">
                <div class="col-lg-8">
                    <form @submit.prevent="saveCardOrder">
                        <ValidationObserver ref="orderform">
                            <div class="card card-body">
                                <div class="row">
                                    <div class="col-6">
                                        <img v-if="branding_logo_url" :src="branding_logo_url" style="width: 150px;" :alt="card_order.Branding">
                                    </div>
                                    <div class="col-6">
                                        <select-with-validation
                                            vid="Branding"
                                            label="Branding"
                                            rules="required"
                                            v-model="card_order.Branding"
                                            :options="brands"
                                            :reduce="dg => dg.tag"
                                            option-label="name"
                                            :clearable="false"
                                            hide-label
                                            class="mb-0"
                                        >
                                            <template #input-group-prepend>
                                                <span class="input-group-text">Branding:</span>
                                            </template>
                                        </select-with-validation>
                                    </div>
                                </div>
                            </div>

                            <div class="mb-3">
                                <div class="btn-group position-static">
                                    <button type="button" class="btn btn-sm btn-secondary btn-labeled btn-labeled-left dropdown-toggle mr-2" data-toggle="dropdown" aria-expanded="false">
                                        <b><i class="icon-mention"></i></b> SKICKA
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-left">
                                        <a href="#" class="dropdown-item" @click="showModalMail('confirmation')">BEKRÄFTELSE</a>
                                        <a href="#" class="dropdown-item" @click="showModalMail('proforma')">PROFORMA</a>
                                        <a href="#" class="dropdown-item" @click="showModalMail('quote')">OFFERT</a>
                                        <a href="#" class="dropdown-item" @click="showModalMail('delivery')">LEVERANSBESKED</a>
                                        <a href="#" class="dropdown-item" @click="showModalMail('message')">MEDDELANDE</a>
                                    </div>
                                </div>
                                <button type="button" class="btn btn-secondary btn-sm mr-2"
                                        @click="openPopupWindow(old_system_url + '/showPDF_DeliveryNote.php?layout=deliverynote&OrderID=' + card_order.OrderID + '&branding=' + card_order.Branding, 'showpdf_deliverynote')">
                                    <i class='icon-file-pdf mr-2'></i>FÖLJESEDEL
                                </button>
                                <button type="button" class="btn btn-secondary btn-sm mr-2"
                                        @click="openPopupWindow(old_system_url + '/showPDF_CardOrder.php?cardorderid=' + card_order.OrderID, 'showpdf_cardorder')">
                                    <i class='icon-file-pdf mr-2'></i>BESTÄLLNING
                                </button>
                            </div>

                            <div class="card">
                                <div class="card-header header-elements-inline bg-light">
                                    <h6 class="card-title"><i class="icon-pencil mr-2"></i> <span class="text-semibold">Redigera kortbeställning</span></h6>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <div class="col-lg-4">
                                            <select-with-validation
                                                label="Betalstatus"
                                                component="card-order-payment-status-filter"
                                                :reduce="(opt) => opt.id"
                                                v-model="card_order.PaymentStatusID"
                                                class="mb-0"
                                                :clearable="false"
                                                rules="required"
                                            ></select-with-validation>
                                        </div>
                                        <div class="col-lg-4">
                                            <select-with-validation
                                                label="Leveransstatus"
                                                component="card-order-delivery-status-filter"
                                                :reduce="(opt) => opt.id"
                                                v-model="card_order.DeliveryStatusID"
                                                class="mb-0"
                                                :clearable="false"
                                                rules="required"
                                            ></select-with-validation>
                                        </div>
                                        <div class="col-lg-4">
                                            <select-with-validation
                                                label="Orderstatus"
                                                :key="order_status_selector_key"
                                                component="card-order-status-filter"
                                                :reduce="(opt) => opt.id"
                                                :value="card_order.StatusID"
                                                @input="changeOrderStatus"
                                                :disabled="confirming_order_status"
                                                class="mb-0"
                                                :clearable="false"
                                                rules="required"
                                            ></select-with-validation>
                                        </div>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <label class="col-form-label col-lg-2 py-0">Ordernummer</label>
                                        <div v-if="card_order.OrderID > 0" class="col-lg-5">
                                            <b>{{ card_order.OrderID }}</b>
                                        </div>
                                        <div v-else class="col-lg-5">
                                            <b>Ny</b>
                                        </div>
                                        <div v-if="card_order.OrderID > 0" class="col-lg-5">
                                            <nobr>
                                                <img alt="*" SRC="/img/barcodes/!asterisk.gif" style="border: 0;">
                                                <img alt="O" SRC="/img/barcodes/O.gif" style="border: 0;">
                                                <img alt="I" SRC="/img/barcodes/I.gif" style="border: 0;">
                                                <img alt="D" SRC="/img/barcodes/D.gif" style="border: 0;">
                                                <img alt="$" SRC="/img/barcodes/!dollar.gif" style="border: 0;">
                                                <img v-for="(char, c_idx) in barcode" :src="'/img/barcodes/' + char + '.gif'" :alt="c_idx" :key="'char-' + c_idx" style="border: 0;">
                                                <img alt="*" SRC="/img/barcodes/!asterisk.gif" style="border: 0;">
                                            </nobr>
                                        </div>
                                    </div>
                                    <div v-if="card_order.OrderID > 0" class="row">
                                        <label class="col-form-label col-lg-2 py-0">Skapad</label>
                                        <div class="col-lg-5">
                                            <a href="" v-if="hasRole('super-admin')" @click.prevent="showModalEditDate">{{ formatDateTime(card_order.CreatedTS) }}</a>
                                            <span v-else>{{ formatDateTime(card_order.CreatedTS) }}</span> / <span id="co-source">{{ card_order.CreatedSource }}</span>
                                            <b-popover key="co-source" target="co-source" triggers="hover focus" placement="bottom" v-if="card_order.CreatedSourceID">
                                                {{ card_order.CreatedSourceID }}
                                            </b-popover>
                                        </div>
                                    </div>
                                    <div v-if="card_order.OrderID > 0" class="row mb-3">
                                        <label class="col-form-label col-lg-2 pt-0">Betalsätt</label>
                                        <div class="col-lg-5">
                                            <span v-if="card_order.PaymentMethod === 'adyen_card'">Adyen Kort</span>
                                            <span v-else-if="card_order.PaymentMethod === 'adyen_swish'">Adyen Swish</span>
                                            <span v-else-if="card_order.PaymentMethod === 'adyen_apple_pay'">Adyen Apple Pay</span>
                                            <span v-else-if="card_order.PaymentMethod === 'adyen_payment_link'">Adyen Betalllänk</span>
                                            <span v-else>Faktura</span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2 pt-0">Kund</label>
                                        <div class="col-lg-5">
                                            <fieldset class="mb-3">
                                                <legend class="text-uppercase font-size-sm font-weight-bold mb-2 border-bottom">Faktura</legend>
                                                <div v-if="card_order.invoice_address">
                                                    <div v-if="card_order.invoice_address.DeliveryCompany">{{ card_order.invoice_address.DeliveryCompany }}</div>
                                                    <div v-if="card_order.invoice_address.DeliveryContactName && card_order.invoice_address.DeliveryContactName !== card_order.invoice_address.DeliveryCompany">
                                                        {{ card_order.invoice_address.DeliveryContactName }}
                                                    </div>
                                                    <div v-if="card_order.invoice_address.DeliveryAddress1">{{ card_order.invoice_address.DeliveryAddress1 }}</div>
                                                    <div v-if="card_order.invoice_address.DeliveryAddress2">{{ card_order.invoice_address.DeliveryAddress2 }}</div>
                                                    <div v-if="card_order.invoice_address.DeliveryZip || card_order.invoice_address.DeliveryCity">
                                                        <span v-if="card_order.invoice_address.DeliveryZip">{{ card_order.invoice_address.DeliveryZip }}</span>
                                                        <span v-if="card_order.invoice_address.DeliveryCity">{{ card_order.invoice_address.DeliveryCity }}</span>
                                                    </div>
                                                    <div v-if="card_order.invoice_address.DeliveryCountryCode">{{ card_order.invoice_address.DeliveryCountryCode }}</div>
                                                </div>
                                                <div v-else-if="card_order.OrderID === 0 && customer.default_invoice_address">
                                                    <div v-if="customer.default_invoice_address.DeliveryCompany">{{ customer.default_invoice_address.DeliveryCompany }}</div>
                                                    <div v-if="customer.default_invoice_address.DeliveryContactName && customer.default_invoice_address.DeliveryContactName !== customer.default_invoice_address.DeliveryCompany">
                                                        {{ customer.default_invoice_address.DeliveryContactName }}
                                                    </div>
                                                    <div v-if="customer.default_invoice_address.DeliveryAddress1">{{ customer.default_invoice_address.DeliveryAddress1 }}</div>
                                                    <div v-if="customer.default_invoice_address.DeliveryAddress2">{{ customer.default_invoice_address.DeliveryAddress2 }}</div>
                                                    <div v-if="customer.default_invoice_address.DeliveryZip || customer.default_invoice_address.DeliveryCity">
                                                        <span v-if="customer.default_invoice_address.DeliveryZip">{{ customer.default_invoice_address.DeliveryZip }}</span>
                                                        <span v-if="customer.default_invoice_address.DeliveryCity">{{ customer.default_invoice_address.DeliveryCity }}</span>
                                                    </div>
                                                    <div v-if="customer.default_invoice_address.DeliveryCountryCode">{{ customer.default_invoice_address.DeliveryCountryCode }}</div>
                                                </div>
                                                <div v-else>
                                                    <i>Ingen fakturaadress vald</i>
                                                </div>
                                            </fieldset>
                                        </div>
                                        <div class="col-lg-5">
                                            <fieldset class="mb-3">
                                                <legend class="text-uppercase font-size-sm font-weight-bold mb-2 border-bottom">Leverans</legend>
                                                <div v-if="card_order.delivery_comment">
                                                    {{ card_order.delivery_comment }}
                                                </div>
                                                <div v-else-if="(delivery_addresses && delivery_addresses.length > 1) || card_order.hasMailDelivery || card_order.hasDigitalDelivery">
                                                    Enligt adresslista
                                                </div>
                                                <div v-else-if="!(delivery_addresses && delivery_addresses.length > 0) && card_order.hasPickupDelivery">
                                                    Hämtas
                                                </div>
                                                <div v-else-if="delivery_addresses && delivery_addresses[0]">
                                                    <div v-if="delivery_addresses[0].name">{{ delivery_addresses[0].name }}</div>
                                                    <div v-if="delivery_addresses[0].co">C/O {{ delivery_addresses[0].co }}</div>
                                                    <div v-if="delivery_addresses[0].street_1">{{ delivery_addresses[0].street_1 }}</div>
                                                    <div v-if="delivery_addresses[0].street_2">{{ delivery_addresses[0].street_2 }}</div>
                                                    <div v-if="delivery_addresses[0].postal_code || delivery_addresses[0].city">
                                                        <span v-if="delivery_addresses[0].postal_code">{{ delivery_addresses[0].postal_code }} </span>
                                                        <span v-if="delivery_addresses[0].city">{{ delivery_addresses[0].city }}</span>
                                                    </div>
                                                    <div v-if="delivery_addresses[0].country_code">{{ delivery_addresses[0].country_code }}</div>
                                                </div>
                                            </fieldset>
                                        </div>
                                    </div>

                                    <div v-if="customer.unpaid_amount > 0" class="alert alert-danger alert-styled-left alert-bordered">
                                        <router-link :to="{ name: 'customers.edit', params: { id: customer.CustomerID } }" style="font-size: 24px; color: #333333;">
                                            OBS! Kunden har förfallna fakturor: {{ formatCurrency((Math.round(parseFloat(customer.unpaid_amount) * 100) / 100), 2, (customer.currency ? customer.currency.CurrencyShort : 'SEK')) }}
                                        </router-link>
                                    </div>

                                    <div v-if="customer.CustomerGroupID_SHT===102 || customer.CustomerGroupID_SHT===112" class="row">
                                        <label class="col-form-label col-lg-2">Slutkund</label>
                                        <div class="col-lg-10">
                                            <input-with-validation
                                                vid="EndCustomer"
                                                label="Slutkund"
                                                v-model="card_order.EndCustomer"
                                                hide-label
                                                compact
                                            />
                                        </div>
                                    </div>
                                    <div class="row">
                                        <label class="col-form-label col-lg-2">Kundens referens</label>
                                        <div class="col-lg-4">
                                            <input-with-validation
                                                vid="CustomerRef"
                                                label="Kundens referens"
                                                v-model="card_order.CustomerRef"
                                                hide-label
                                                compact
                                            />
                                        </div>
                                    </div>
                                    <div class="row">
                                        <label class="col-form-label col-lg-2">Kundens ordernr</label>
                                        <div class="col-lg-4">
                                            <input-with-validation
                                                vid="CustomerRefNo"
                                                label="Kundens ordernr"
                                                v-model="card_order.CustomerRefNo"
                                                hide-label
                                                compact
                                            />
                                        </div>
                                    </div>
                                    <div class="row">
                                        <label class="col-form-label col-lg-2">Kundens fakturareferensnr</label>
                                        <div class="col-lg-4">
                                            <input-with-validation
                                                vid="CustomerInvoiceRefNo"
                                                label="Kundens fakturareferensnr"
                                                v-model="card_order.CustomerInvoiceRefNo"
                                                hide-label
                                                compact
                                            />
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">
                                            Låst språk
                                            <i class="icon-question3 ml-1" :id="'pop-ForceLanguage'"></i>
                                            <b-popover :key="'pop-ForceLanguage'" :target="'pop-ForceLanguage'" triggers="hover focus">
                                                <div>Förvalt språk vid inlösen av kort</div>
                                            </b-popover>
                                        </label>
                                        <div class="col-lg-4">
                                            <select-with-validation
                                                vid="ForceLanguage"
                                                label="Låst språk"
                                                v-model="card_order.ForceLanguage"
                                                :options="languages"
                                                :reduce="lang => lang.short"
                                                option-label="name"
                                                placeholder="Inget valt språk"
                                                hide-label
                                            />
                                        </div>
                                        <label class="col-form-label col-lg-2 text-right">
                                            Leveransgrupp
                                            <i class="icon-question3 ml-1" :id="'pop-override_delivery_group'"></i>
                                            <b-popover :key="'pop-override_delivery_group'" :target="'pop-override_delivery_group'" triggers="hover focus">
                                                <div>Anger vilka länder som kan väljas när man löser in sitt kort</div>
                                            </b-popover>
                                        </label>
                                        <div class="col-lg-4">
                                            <select-with-validation
                                                component="delivery-group-filter"
                                                vid="override_delivery_group"
                                                label="Leveransgrupp"
                                                v-model="card_order.override_delivery_group"
                                                :reduce="dg => dg.id"
                                                placeholder="Följer kortserie"
                                                hide-label
                                            />
                                        </div>
                                    </div>

<!--                                                <ValidationProvider vid="CardTypeID" name="Korttyp" rules="required" v-slot="{ errors }">-->
<!--                                                    <limitless-select2 :options="select_card_order_types" v-model="card_order.CardTypeID" :has_error="(!!errors[0])"></limitless-select2>-->
<!--                                                    <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>-->
<!--                                                </ValidationProvider>-->

                                </div> <!-- /card-body -->
                                <div class="card-header bg-light border-top">
                                    <h6 class="card-title"><i class="icon-user mr-2"></i> <span class="text-semibold">Kontaktperson</span></h6>
                                </div>
                                <div class="card-body">
                                    <div class="form-group row mb-2">
                                        <label class="col-form-label col-lg-2">Kontaktperson</label>
                                        <div class="col-lg-8">
                                            <select-with-validation
                                                vid="ContactID"
                                                label="Kontaktperson"
                                                v-model="card_order.ContactID"
                                                :options="select_contacts"
                                                :reduce="c => c.id"
                                                placeholder="Välj kontaktperson"
                                                hide-label
                                            />
                                        </div>
                                        <div class="col-lg-2">
                                            <button
                                                type="button"
                                                class="btn btn-block btn-secondary"
                                                @click="edit_contact_id = (card_order.ContactID > 0 ? card_order.ContactID : 0)"
                                            >
                                                {{ (card_order.ContactID > 0 ? 'Redigera' : 'Lägg till ny') }}
                                            </button>
                                        </div>
                                    </div>
                                    <div class="form-group row mb-2">
                                        <label class="col-form-label col-lg-2">Namn</label>
                                        <div class="col-lg-10">
                                            <input type="text" class="form-control" :value="card_order.CustomerContact" readonly>
                                        </div>
                                    </div>
                                    <div class="form-group row mb-2">
                                        <label class="col-form-label col-lg-2">Telefon</label>
                                        <div class="col-lg-10">
                                            <input type="text" class="form-control" :value="card_order.CustomerContactPhone" readonly>
                                        </div>
                                    </div>
                                    <div class="form-group row mb-2">
                                        <label class="col-form-label col-lg-2">E-post</label>
                                        <div class="col-lg-10">
                                            <input type="text" class="form-control" :value="card_order.CustomerContactMail" readonly>
                                        </div>
                                    </div>
                                </div> <!-- /card-body -->
                                <div class="card-header bg-light border-top">
                                    <h6 class="card-title"><i class="icon-truck mr-2"></i> <span class="text-semibold">Leverans</span></h6>
                                </div>
                                <div class="card-body">
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Leveranssätt</label>
                                        <div class="col-lg-3">
                                            <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                <input id="chk-hasDigitalDelivery" type="checkbox" class="custom-control-input" v-model="card_order.hasDigitalDelivery" :true-value="1" :false-value="0">
                                                <label for="chk-hasDigitalDelivery" class="custom-control-label">Digitalt utskick</label>
                                            </div>
                                        </div>
                                        <div class="col-lg-3">
                                            <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                <input id="chk-hasMailDelivery" type="checkbox" class="custom-control-input" v-model="card_order.hasMailDelivery" :true-value="1" :false-value="0">
                                                <label for="chk-hasMailDelivery" class="custom-control-label">Postalt utskick</label>
                                            </div>
                                        </div>
                                        <div class="col-lg-3">
                                            <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                <input id="chk-hasPickupDelivery" type="checkbox" class="custom-control-input" v-model="card_order.hasPickupDelivery" :true-value="1" :false-value="0">
                                                <label for="chk-hasPickupDelivery" class="custom-control-label">Hämtas</label>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group row mb-2">
                                        <label class="col-form-label col-lg-2">Sändningsdatum</label>
                                        <ValidationProvider tag="div" class="col-lg-4" vid="CalcShippingDate" name="Sändningsdatum" rules="" v-slot="{ errors }">
                                            <v-date-picker v-model="card_order.CalcShippingDate" mode="date" :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }" :masks="{ input: 'YYYY-MM-DD' }" show-iso-weeknumbers>
                                                <template v-slot="{ inputValue, togglePopover }">
                                                    <div class="input-group">
                                                        <input class="form-control" :value="inputValue" @focus="togglePopover()">
                                                        <span class="input-group-append">
                                                            <button class="btn btn-light" type="button" @click="card_order.CalcShippingDate = ''"><i class="icon-cross3"></i></button>
                                                        </span>
                                                    </div>
                                                </template>
                                            </v-date-picker>
                                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                        </ValidationProvider>
                                        <div class="col-lg-4 col-form-label">
                                            <span class="badge bg-teal text-uppercase text-white" v-if="send_date_str" v-html="send_date_str"></span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Önskat lev. datum</label>
                                        <ValidationProvider tag="div" class="col-lg-4" vid="DeliveryDate" name="Leveransdatum" rules="" v-slot="{ errors }">
                                            <v-date-picker v-model="card_order.DeliveryDate" mode="date" :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }" :masks="{ input: 'YYYY-MM-DD' }" show-iso-weeknumbers>
                                                <template v-slot="{ inputValue, togglePopover }">
                                                    <div class="input-group mb-0">
                                                        <input class="form-control" :value="inputValue" @focus="togglePopover()">
                                                        <span class="input-group-append">
                                                            <button class="btn btn-light" type="button" @click="card_order.DeliveryDate = ''"><i class="icon-cross3"></i></button>
                                                        </span>
                                                    </div>
                                                </template>
                                            </v-date-picker>
                                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                        </ValidationProvider>
                                        <div class="col-lg-4 col-form-label">
                                            <span class="badge bg-teal text-uppercase text-white" v-if="deliv_date_str" v-html="deliv_date_str"></span>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Leveranskommentar</label>
                                        <ValidationProvider tag="div" class="col-lg-10" vid="delivery_comment" name="Leveranskommentar" rules="" v-slot="{ errors }">
                                            <input type="text" v-model="card_order.delivery_comment" class="form-control">
                                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                        </ValidationProvider>
                                    </div>
                                </div> <!-- /card-body -->
                                <div class="card-header header-elements-inline bg-light border-top">
                                    <h6 class="card-title"><i class="icon-truck mr-2"></i> <span class="text-semibold">Leveransadresser</span></h6>
                                    <div v-if="card_order.OrderID > 0" class="header-elements">
                                        <a
                                            href="/storage/mall_adresslista.xlsx"
                                            class="btn btn-sm btn-light mr-2"
                                            download=""
                                        >
                                            <i class="icon-file-download mr-1"></i>Ladda ner mall
                                        </a>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-light mr-2"
                                            @click="showUploadAddressList"
                                        >
                                            <i class="icon-file-upload mr-1"></i>Ladda upp lista
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-light"
                                            @click="showModalEditAddress()"
                                        >
                                            <i class="icon-plus3 mr-1"></i>Lägg till leveransadress
                                        </button>
                                    </div>
                                </div>
                                <div v-if="card_order.OrderID > 0">
                                    <div
                                        v-if="delivery_addresses.length > 0"
                                    >
                                        <div
                                            style="max-height: 600px; overflow-y: scroll;"
                                        >
                                            <table class="table table-sm" style="border-collapse: collapse;">
                                                <thead>
                                                    <tr>
                                                        <th class="border-top-0 bg-white" style="position: sticky; top: 0; z-index: 2;">Adress</th>
                                                        <th class="border-top-0 bg-white" style="position: sticky; top: 0; z-index: 2;">Kontakt</th>
                                                        <th class="border-top-0 bg-white" style="position: sticky; top: 0; z-index: 2;">Fraktsätt</th>
                                                        <th class="border-top-0 bg-white" style="position: sticky; top: 0; z-index: 2;">Produkter</th>
                                                        <th class="border-top-0 bg-white" style="position: sticky; top: 0; z-index: 2;">&nbsp;</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr
                                                        v-for="(addr, addr_index) in delivery_addresses"
                                                        :key="'da-' + addr_index"
                                                    >
                                                        <td>
                                                            <div v-if="addr.name">{{ addr.name }}</div>
                                                            <div v-if="addr.co">{{ addr.co }}</div>
                                                            <div v-if="addr.street_1">{{ addr.street_1 }}</div>
                                                            <div v-if="addr.street_2">{{ addr.street_2 }}</div>
                                                            <div v-if="addr.postal_code || addr.city">{{ addr.postal_code }} {{ addr.city }}</div>
                                                            <div v-if="addr.country">{{ addr.country.Country_SE }}</div>
                                                            <div v-else>{{ addr.country_code }}</div>
                                                        </td>
                                                        <td>
                                                            <div v-if="addr.contact_name">{{ addr.contact_name }}</div>
                                                            <div v-if="addr.contact_phone">{{ addr.contact_phone }}</div>
                                                            <div v-if="addr.contact_email">{{ addr.contact_email }}</div>
                                                        </td>
                                                        <td>
                                                            <div v-if="addr.printed" class=""><i class="icon-checkmark4 text-success mr-1"></i>Utskriven</div>
                                                            <div v-if="addr.delivery_time_string" style="font-weight: bold; color: #990000;">{{ addr.delivery_time_string }}</div>
                                                            <div v-if="addr.service_type">{{ addr.service_type }}</div>
                                                            <div v-if="addr.transport_instruction">{{ addr.transport_instruction }}</div>
                                                            <div v-if="addr.pack_instruction">{{ addr.pack_instruction }}</div>
                                                        </td>
                                                        <td>
                                                            <div
                                                                v-for="(item, i_index) in addr.items"
                                                                :key="'da-' + addr_index + '-i-' + i_index"
                                                            >
                                                                {{ item.sku }}: {{ item.quantity }}
                                                            </div>
                                                        </td>
                                                        <td style="width: 250px;">
                                                            <div class="mb-1">
                                                                <button
                                                                    type="button"
                                                                    class="btn btn-secondary btn-sm mr-1"
                                                                    @click="$router.push({ name: 'shipping.print_label', query: { card_order_id: card_order.OrderID, delivery_address_id: addr.id } })"
                                                                    :disabled="!(addr.name && addr.city && addr.postal_code && addr.country_code && (addr.street_1 || addr.street_2))"
                                                                >
                                                                    <i class="icon-printer2 mr-2"></i>STOR
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    class="btn btn-sm btn-secondary mr-1"
                                                                    @click="showModalStockLabel(card_order.OrderID, addr.id)"
                                                                    :disabled="!(addr.name && addr.city && addr.postal_code && addr.country_code && (addr.street_1 || addr.street_2))"
                                                                >
                                                                    <i class="icon-printer2 mr-2"></i>LITEN
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    class="btn btn-sm btn-light"
                                                                    @click="showModalEditAddress(addr)"
                                                                >
                                                                    <i class="icon-pencil"></i>
                                                                </button>
                                                            </div>
                                                            <a
                                                                v-if="addr.country_code !== 'SE' && Array.isArray(addr.items) && addr.items.length > 0"
                                                                :href="'/cardorders/' + card_order.OrderID + '/pdf/commercial/' + addr.id"
                                                                target="_blank"
                                                            >Commercial invoice</a>
                                                            <template
                                                                v-for="ap in ntaps_parcels.filter((p) => p.shipment.internal_reference3 == addr.id)"
                                                            >
                                                                <div
                                                                    v-if="!ap.shipment.deleted_at"
                                                                    :key="'da-' + addr_index + '-' + ap.parcel_number"
                                                                    class="mt-1"
                                                                >
                                                                    <a
                                                                        v-if="ap.shipment.service_type.indexOf('UPS-') === 0"
                                                                        :href="'https://www.ups.com/track?loc=en_SE&tracknum=' + ap.parcel_number + '&requester=ST/trackdetails'"
                                                                        :id="'da-' + addr_index + '-link-' + ap.parcel_number"
                                                                        target="_blank"
                                                                    >{{ ap.parcel_number }}</a>
                                                                    <a
                                                                        v-else
                                                                        :href="'https://www.postnord.se/vara-verktyg/spara-brev-paket-och-pall?shipmentId=' + ap.parcel_number"
                                                                        :id="'da-' + addr_index + '-link-' + ap.parcel_number"
                                                                        target="_blank"
                                                                    >{{ ap.parcel_number }}</a>
                                                                    <b-popover key="warning-out-of-stock" :target="'da-' + addr_index + '-link-' + ap.parcel_number" triggers="hover focus" placement="bottom">
                                                                        <div>{{ ap.status ? ap.status.StatusDesc : 'Status saknas' }}</div>
                                                                        <div v-if="ap.delivered_at">Levererat: {{ formatDateTime(ap.delivered_at) }}</div>
                                                                        <div v-if="ap.acceptor">Mottaget av: {{ ap.acceptor }}</div>
                                                                    </b-popover>
                                                                </div>
                                                            </template>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                        <div class="card-body d-flex flex-row border-top">
                                            <div class="mr-auto">
                                                Totalt {{ delivery_addresses.length }} st adresser
                                            </div>
                                            <button
                                                type="button"
                                                class="btn btn-light mr-2"
                                                @click="showModalStockLabel(card_order.OrderID)"
                                            >
                                                <i class="icon-printer2 mr-2"></i>Skriv ut alla (Liten)
                                            </button>
                                            <a
                                                :href="'/batch/list?type=card_order_delivery_address&card_order_id=' + card_order.OrderID"
                                                class="btn btn-light"
                                            >
                                                <i class="icon-printer2 mr-2"></i>Skriv ut alla (STOR)
                                            </a>
                                        </div>
                                    </div>
                                    <div v-else class="card-body">
                                        <i class="text-danger font-weight-semibold">Ingen leveransadress</i>
                                    </div> <!-- /card-body -->
                                </div>
                                <div v-else class="card-body">
                                    <i class="text-danger font-weight-semibold">Skapa ordern innan leveransadress matas in</i>
                                </div>
                                <div class="card-header bg-light border-top">
                                    <h6 class="card-title"><i class="icon-truck mr-2"></i> <span class="text-semibold">Utskrivna fraktsedlar</span></h6>
                                </div>
                                <div v-if="unifaun_data.length > 0 || ntaps_parcels.length > 0">
                                    <table class="table table-xs">
                                        <tbody>
                                            <template v-if="unifaun_data.length > 0">
                                                <tr v-for="(up, up_index) in unifaun_data" :key="'ufd-' + up_index" v-bind:style="{ 'text-decoration': (up.isHidden ? 'line-through' : 'none') }">
                                                    <td :class="{ 'text-line-through': up.isHidden }">{{ formatDateTime(up.printdate) }}</td>
                                                    <td :class="{ 'text-line-through': up.isHidden }">{{ up.kollino }}</td>
                                                    <td :class="{ 'text-line-through': up.isHidden }">
                                                        <a :href="'https://www.postnord.se/vara-verktyg/spara-brev-paket-och-pall?shipmentId=' + up.kollino" :id="'link-' + up.kollino">[PT] {{ up.status ? up.status.StatusDesc : 'Status saknas' }}</a>
                                                        <b-popover key="warning-out-of-stock" :target="'link-' + up.kollino" triggers="hover focus" placement="bottom" v-if="up.pt_datedelivered || up.pt_acceptor">
                                                            <div v-if="up.pt_datedelivered">Levererat: {{ formatDateTime(up.pt_datedelivered) }}</div>
                                                            <div v-if="up.pt_acceptor">Mottaget av: {{ up.pt_acceptor }}</div>
                                                        </b-popover>
                                                    </td>
                                                    <td><input type="checkbox" v-model="hide_kolli" :value="up.kollino"></td>
                                                </tr>
                                            </template>
                                            <template v-if="ntaps_parcels.length > 0">
                                                <tr v-for="(np, np_index ) in ntaps_parcels" :key="'np-' + np_index">
                                                    <td :class="{ 'text-line-through': !!(np.shipment.deleted_at) }">{{ formatDateTime(np.shipment.created_at) }}</td>
                                                    <td :class="{ 'text-line-through': !!(np.shipment.deleted_at) }">{{ np.parcel_number }}</td>
                                                    <td :class="{ 'text-line-through': !!(np.shipment.deleted_at) }">
                                                        <a
                                                            v-if="np.shipment.service_type.indexOf('UPS-') === 0"
                                                            :href="'https://www.ups.com/track?loc=en_SE&tracknum=' + np.parcel_number + '&requester=ST/trackdetails'"
                                                            :id="'link-' + np.parcel_number"
                                                            target="_blank"
                                                        >[PT] {{ np.status ? np.status.StatusDesc : 'Status saknas' }}</a>
                                                        <a
                                                            v-else
                                                            :href="'https://www.postnord.se/vara-verktyg/spara-brev-paket-och-pall?shipmentId=' + np.parcel_number"
                                                            :id="'link-' + np.parcel_number"
                                                            target="_blank"
                                                        >[PT] {{ np.status ? np.status.StatusDesc : 'Status saknas' }}</a>
                                                        <b-popover key="warning-out-of-stock" :target="'link-' + np.parcel_number" triggers="hover focus" placement="bottom" v-if="np.delivered_at || np.acceptor">
                                                            <div v-if="np.delivered_at">Levererat: {{ formatDateTime(np.delivered_at) }}</div>
                                                            <div v-if="np.acceptor">Mottaget av: {{ np.acceptor }}</div>
                                                        </b-popover>
                                                    </td>
                                                    <td><input type="checkbox" v-model="hide_kolli" :value="np.parcel_number"></td>
                                                </tr>
                                            </template>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-else class="card-body">
                                    <i>Inga kollinummer registrerade</i>
                                </div> <!-- /card-body -->
                                <div class="card-header bg-light border-top header-elements-inline">
                                    <h6 class="card-title"><i class="icon-cog mr-2"></i> <span class="text-semibold">Tilläggstjänster</span></h6>
                                </div>
                                <div class="card-body">
                                    <div class="row">
                                        <label class="col-form-label col-lg-2">Tilläggstjänster</label>
                                        <div class="col-lg-10">
                                            <div class="row">
                                                <div class="col-lg-3">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-hasKuvertering" type="checkbox" class="custom-control-input" v-model="card_order.hasKuvertering" :true-value="1" :false-value="0">
                                                        <label for="chk-hasKuvertering" class="custom-control-label">Kuvertering</label>
                                                    </div>
                                                </div>
                                                <div class="col-lg-3">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-hasCustomPrint" type="checkbox" class="custom-control-input" v-model="card_order.hasCustomPrint" :true-value="1" :false-value="0">
                                                        <label for="chk-hasCustomPrint" class="custom-control-label">TRYCKT hälsning</label>
                                                    </div>
                                                </div>
                                                <div class="col-lg-3">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-hasCustomWeb" type="checkbox" class="custom-control-input" v-model="card_order.hasCustomWeb" :true-value="1" :false-value="0">
                                                        <label for="chk-hasCustomWeb" class="custom-control-label">WEBB-hälsning</label>
                                                    </div>
                                                </div>
                                                <div class="col-lg-3">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-PrivateLable" type="checkbox" class="custom-control-input" v-model="card_order.PrivateLable" :true-value="1" :false-value="0">
                                                        <label for="chk-PrivateLable" class="custom-control-label">Eget kort</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <template v-if="card_order.hasCustomPrint || card_order.hasCustomWeb">
                                    <div class="card-header bg-light border-top header-elements-inline">
                                        <h6 class="card-title"><i class="icon-printer2 mr-2"></i> <span class="text-semibold">Tryckinformation</span></h6>
                                    </div>
                                    <div class="card-body">
                                        <div class="form-group row">
                                            <label class="col-form-label col-lg-2">Korr</label>
                                            <div class="col-lg-10">
                                                <div class="row">
                                                    <div class="col-lg-3">
                                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                            <input id="chk-needKorr" type="checkbox" class="custom-control-input" v-model="card_order.needKorr" :true-value="1" :false-value="0">
                                                            <label for="chk-needKorr" class="custom-control-label">KORR innan tryck/leverans</label>
                                                        </div>
                                                    </div>
                                                    <div class="col-lg-3">
                                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                            <input id="chk-korrSent" type="checkbox" class="custom-control-input" v-model="card_order.korrSent" :true-value="1" :false-value="0">
                                                            <label for="chk-korrSent" class="custom-control-label">Korr skickat</label>
                                                        </div>
                                                    </div>
                                                    <div class="col-lg-3">
                                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                            <input id="chk-korrAccepted" type="checkbox" class="custom-control-input" :checked="!!production_files.approved" disabled>
                                                            <label for="chk-korrAccepted" class="custom-control-label">Korr godkänt</label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            v-if="hasGreetingImage"
                                            class="form-group row"
                                        >
                                            <label class="col-form-label col-lg-2">Hälsning</label>
                                            <div class="col-lg-3">
                                                <mediabank-selector
                                                    v-model="card_order.GreetingImageID"
                                                    @selected="greetingImageSelected"
                                                    btn-class="btn-sm btn-secondary"
                                                    btn-text="Välj bildhälsning"
                                                    btn-icon="icon-image2"
                                                    :category-id="1005"
                                                    category-locked
                                                    thumbnail
                                                    upload
                                                ></mediabank-selector>
                                            </div>
                                        </div>
                                        <div class="row mb-0" v-else>
                                            <label class="col-form-label col-lg-2">Hälsning</label>
                                            <div class="col-lg-10">
                                                <div class="row">
                                                    <div class="col-lg-4">
                                                        <select-with-validation
                                                            vid="GreetingFont"
                                                            label="Font"
                                                            v-model="card_order.GreetingFont"
                                                            :reduce="c => c.id"
                                                            :placeholder="'Välj font'"
                                                            :clearable="false"
                                                            component="ecard-template-fonts-filter"
                                                        />
                                                    </div>
                                                    <div class="col-lg-4">
                                                        <select-with-validation
                                                            vid="GreetingSize"
                                                            label="Textstorlek"
                                                            v-model="card_order.GreetingSize"
                                                            :reduce="(opt) => opt.id"
                                                            component="font-size-filter"
                                                            :clearable="false"
                                                        ></select-with-validation>
                                                    </div>
                                                    <div class="col-lg-4">
                                                        <color-picker
                                                            vid="GreetingColor"
                                                            label="Textfärg"
                                                            v-model="card_order.GreetingColor"
                                                        ></color-picker>
                                                    </div>
                                                </div>
                                                <div class="row mb-1">
                                                    <div class="col-lg-8">
                                                        <textarea-with-validation
                                                            vid="GreetingText"
                                                            label="Hälsning"
                                                            :min-height="90"
                                                            v-model="card_order.GreetingText"
                                                            placeholder="Skriv en hälsning..."
                                                            compact
                                                        />
                                                    </div>
                                                    <div class="col-lg-4">
                                                        <label>Logo</label>
                                                        <mediabank-selector
                                                            v-model="card_order.GreetingLogoID"
                                                            btn-class="btn-sm btn-secondary"
                                                            btn-text="Välj logotyp"
                                                            btn-icon="icon-image2"
                                                            :category-id="1004"
                                                            category-locked
                                                            thumbnail
                                                            upload
                                                        ></mediabank-selector>
                                                    </div>
                                                </div>
                                                <mediabank-selector
                                                    v-model="card_order.GreetingImageID"
                                                    @selected="greetingImageSelected"
                                                    btn-class="btn-sm btn-secondary"
                                                    btn-text="Välj bildhälsning"
                                                    btn-icon="icon-image2"
                                                    :category-id="1005"
                                                    category-locked
                                                    thumbnail
                                                    upload
                                                ></mediabank-selector>
                                            </div>
                                        </div>
                                        <order-production-flow
                                            v-if="Number(id) > 0" :order-id="id" :state="productionStatus"
                                            :can-design="!hasGreetingImage || (!greeting_image_selected && production_files.proof_source === 'draft')" :manual-image="!!manualGreetingImage"
                                            @updated="productionFilesUpdated" @create-proof="showModalCreateKorr" @history="showModalCreatePrint('print')" @create-print="showModalCreatePrint('print')"
                                        />
                                    </div> <!-- /card-body -->
                                </template>
                                <div class="card-header bg-light border-top">
                                    <h6 class="card-title"><i class="icon-coins mr-2"></i> <span class="text-semibold">Fakturering</span></h6>
                                </div>
                                <div class="card-body">
                                    <div
                                        v-if="!customer.Invoice_E_Setting || !customer.Invoice_E_Email"
                                        class="alert alert-warning alert-styled-left alert-bordered font-weight-semibold"
                                    >
                                        {{ customer.Invoice_E_Setting ? 'OBS! Kunden verkar sakna e-postadress för faktura!' : 'OBS! E-faktura ej aktiverat på kunden!' }}
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Betalsätt</label>
                                        <div class="col-lg-5">
                                            <select-with-validation
                                                vid="PaymentMethod"
                                                label="Betalsätt"
                                                v-model="card_order.PaymentMethod"
                                                component="payment-method-filter"
                                                :reduce="(opt) =>opt.id"
                                                :placeholder="'Välj betalsätt'"
                                                rules="required"
                                                hide-label
                                            >
                                                <template #input-group-prepend>
                                                    <span class="input-group-text">Välj:</span>
                                                </template>
                                            </select-with-validation>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Fakturaadress</label>
                                        <div class="col-lg-10">
                                            <select-with-validation
                                                vid="DelivAdrID_Invoicing"
                                                label="Fakturaadress"
                                                v-model="card_order.DelivAdrID_Invoicing"
                                                :options="select_invoice_addresses"
                                                :reduce="c => c.id"
                                                :placeholder="'Välj fakturaaddress'"
                                                hide-label
                                                :rules="{ required: select_invoice_addresses.length > 0 }"
                                            >
                                                <template #input-group-prepend>
                                                    <span class="input-group-text">Välj:</span>
                                                </template>
                                            </select-with-validation>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Betalningsvillkor</label>
                                        <div class="col-lg-4">
                                            <select-with-validation
                                                label="Betalningsvillkor"
                                                vid="PayDays"
                                                v-model="card_order.PayDays"
                                                hide-label
                                                component="pay-days-filter"
                                                :reduce="(opt) => opt.id"
                                            >
                                            </select-with-validation>
                                        </div>
                                        <div class="col-lg-6 col-form-label">Kundens betalvillkor: {{ customer.PayDays }} dagar</div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Villkor</label>
                                        <div class="col-lg-10">
                                            <div class="row">
                                                <div class="col-lg-4">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-invoiceSpecialTerms" type="checkbox" class="custom-control-input" v-model="card_order.invoiceSpecialTerms" :true-value="1" :false-value="0">
                                                        <label for="chk-invoiceSpecialTerms" class="custom-control-label">Specialvillkor</label>
                                                    </div>
                                                </div>
                                                <div class="col-lg-4">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-invoiceConsignation" type="checkbox" class="custom-control-input" v-model="card_order.invoiceConsignation" :true-value="1" :false-value="0">
                                                        <label for="chk-invoiceConsignation" class="custom-control-label">Konsignation</label>
                                                    </div>
                                                </div>
                                                <div class="col-lg-4">
                                                </div>
                                            </div>
                                            <div class="row">
                                                <div class="col-lg-4">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-Ramavtalsorder" type="checkbox" class="custom-control-input" v-model="card_order.Ramavtalsorder" :true-value="1" :false-value="0">
                                                        <label for="chk-Ramavtalsorder" class="custom-control-label">Ramavtal</label>
                                                    </div>
                                                </div>
                                                <div class="col-lg-4">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-wasInvoiceConsignation" type="checkbox" class="custom-control-input" v-model="card_order.wasInvoiceConsignation" :true-value="1" :false-value="0">
                                                        <label for="chk-wasInvoiceConsignation" class="custom-control-label">Avräknad konsignation</label>
                                                    </div>
                                                </div>
                                                <div class="col-lg-4">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-invoiceSvefaktura" type="checkbox" class="custom-control-input" v-model="card_order.invoiceSvefaktura" :true-value="1" :false-value="0" disabled>
                                                        <label for="chk-invoiceSvefaktura" class="custom-control-label">Svefaktura</label>
                                                        <i class="icon-question3 ml-1" :id="'pop-invoiceSvefaktura'"></i>
                                                        <b-popover :key="'pop-invoiceSvefaktura'" :target="'pop-invoiceSvefaktura'" triggers="hover focus">
                                                            <div>Ändras på kund</div>
                                                        </b-popover>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">
                                            Betallänkar
                                            <i class="icon-question3 ml-1" :id="'pop-paylink'"></i>
                                            <b-popover :key="'pop-paylink'" :target="'pop-paylink'" triggers="hover focus">
                                                <div class="mb-2">Skapa en betallänk för att låta kunden göra en förskottsbetalning via kort, Apple Pay eller Swish.</div>
                                                <div class="mb-2">Om du skapar en betallänk och sedan skickar en proforma så bifogas betallänken i mailet.</div>
                                                <div>Betallänken är giltig i 7 dagar.</div>
                                            </b-popover>
                                        </label>
                                        <div class="col-lg-10">
                                            <div v-if="payment_links.length > 0" class="card card-table mb-0">
                                                <table class="table table-framed table-striped table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>ID</th>
                                                            <th>Skapad</th>
                                                            <th>Giltig t.o.m.</th>
                                                            <th>Länk</th>
                                                            <th>Status</th>
                                                            <th style="text-align: right;">Belopp</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(payLink, pl_idx) in payment_links" :key="'payment-link-' + pl_idx">
                                                            <td>{{ payLink.payment_link_id }}</td>
                                                            <td>{{ formatDateTime(payLink.created_at).substring(0, 10) }}</td>
                                                            <td>{{ formatDateTime(payLink.expires_at).substring(0, 10) }}</td>
                                                            <td><a :href="payLink.url" target="_blank">Länk</a></td>
                                                            <td>
                                                                <span v-if="payLink.status === 'expired'" class="text-danger">Utgången</span>
                                                                <span v-else-if="payLink.status === 'completed'" class="text-success">Betald</span>
                                                                <span v-else-if="payLink.status === 'active'" class="text-info">Aktiv</span>
                                                                <span v-else>{{ payLink.status }}</span>
                                                            </td>
                                                            <td style="text-align: right;">{{ parseFloat(payLink.value).toLocaleString('se-SV', { minimumFractionDigits: 2 }) }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div class="col-form-label" v-else><i>Inga betallänkar</i></div>
                                            <limitless-button
                                                v-if="!(card_order.invoices.length > 0)"
                                                ref="btncreatepaylink"
                                                btn-class="btn-info"
                                                class="mt-3"
                                                @click="createPaymentLink"
                                                :busy="busy_create_paylink"
                                            >Skapa betallänk</limitless-button>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">
                                            Betalningar
                                            <i class="icon-question3 ml-1" :id="'pop-payments'"></i>
                                            <b-popover :key="'pop-payments'" :target="'pop-payments'" triggers="hover focus">
                                                <div>Inkomna förskottsbetalningar samt dragningar till fakturor.</div>
                                            </b-popover>
                                        </label>
                                        <div class="col-lg-10">
                                            <div v-if="order_payments.length > 0" class="card card-table mb-0">
                                                <table class="table table-framed table-striped table-sm">
                                                    <thead>
                                                        <tr>
                                                            <th>Datum</th>
                                                            <th>Källa</th>
                                                            <th>Referens</th>
                                                            <th style="text-align: right;">Belopp</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(payment, p_idx) in order_payments" :key="'payment-' + p_idx">
                                                            <td>{{ formatDateTime(payment.payment_date).substring(0, 10) }}</td>
                                                            <td>{{ payment.source }}{{ (payment.source_id ? ' (' + payment.source_id + ')' : '') }}</td>
                                                            <td>{{ payment.reference }}</td>
                                                            <td style="text-align: right;">{{ parseFloat(payment.amount).toLocaleString('se-SV', { minimumFractionDigits: 2 }) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th colspan="2">TOTALT INBETALT</th>
                                                            <td colspan="2" style="text-align: right;">{{ order_payments_total_paid.toLocaleString('se-SV', { minimumFractionDigits: 2 }) }}</td>
                                                        </tr>
                                                        <tr>
                                                            <th colspan="2">TOTALT DRAGET TILL FAKTUROR</th>
                                                            <td colspan="2" style="text-align: right;">{{ order_payments_total_captured_invoices.toLocaleString('se-SV', { minimumFractionDigits: 2 }) }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div class="col-form-label" v-else><i>Inga betalningar</i></div>
                                            <button type="button" class="btn btn-info mt-3" @click="showModalRegisterPayment">Registrera betalning</button>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Fakturor</label>
                                        <div class="col-lg-10">
                                            <div v-if="card_order.invoices.length > 0 || card_order.GrayInvoiceID">
                                                <div v-if="card_order.invoices.length > 0" class="card card-table mb-0">
                                                    <table class="table table-framed table-striped table-sm">
                                                        <thead>
                                                            <tr>
                                                                <th>Nummer</th>
                                                                <th>Datum</th>
                                                                <th>Info</th>
                                                                <th style="text-align: right;">Belopp</th>
                                                                <th style="text-align: right;">Betalt</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr v-for="(invoice, i_idx) in card_order.invoices" :key="'invoice-' + i_idx">
                                                                <td>
                                                                    <router-link :to="{ name: 'invoice.edit', params: { id: invoice.InvoiceID } }">
                                                                        {{ invoice.ocr }}
                                                                    </router-link>
                                                                </td>
                                                                <td>{{ invoice.InvoiceDate }}</td>
                                                                <td>{{ (invoice.isAccepted ? '' : '[utskrift ännu ej godkänd]') }}</td>
                                                                <td style="text-align: right;">
                                                                    {{ (invoice.InvoiceSumFull ? formatCurrency(Math.round(invoice.InvoiceSumFull), 2, (customer.currency ? customer.currency.CurrencyShort : 'SEK')) : '0,00') }}
                                                                </td>
                                                                <td style="text-align: right;">
                                                                    {{ (invoice.PaidAmount ? formatCurrency(invoice.PaidAmount, 2, (customer.currency ? customer.currency.CurrencyShort : 'SEK')) : '0,00') }}
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                                <div v-if="card_order.GrayInvoiceID">
                                                    <a :href="old_gray_system_url + '/invoice.php?id=' + card_order.GrayInvoiceID" target="_blank">Fakturerad i det grå</a>
                                                </div>
                                            </div>
                                            <div class="col-form-label" v-else><i>Inga fakturor</i></div>
                                        </div>
                                    </div>
                                </div> <!-- /card-body -->
                                <div class="card-header bg-light border-top">
                                    <h6 class="card-title"><i class="icon-compose mr-2"></i> <span class="text-semibold">Övrigt</span></h6>
                                </div>
                                <div class="card-body">
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Orderflaggor</label>
                                        <div class="col-lg-10">
                                            <div class="row">
                                                <div class="col-lg-4">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-OrderType" type="checkbox" class="custom-control-input" v-model="card_order.OrderType" :true-value="1" :false-value="0">
                                                        <label for="chk-OrderType" class="custom-control-label">Laxorder</label>
                                                    </div>
                                                </div>
                                                <div v-for="(flag, f_idx) in order_flags" :key="'flag-' + f_idx" class="col-lg-4">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input :id="'chk-flag-' + f_idx" type="checkbox" class="custom-control-input" v-model="card_order.FlagID" :value="flag.id">
                                                        <label :for="'chk-flag-' + f_idx" class="custom-control-label">{{ flag.name }}</label>
                                                    </div>
                                                </div>
                                                <div class="col-lg-4">
                                                    <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                        <input id="chk-allow_portal_addons" type="checkbox" class="custom-control-input" v-model="card_order.allow_portal_addons" :true-value="1" :false-value="0">
                                                        <label for="chk-allow_portal_addons" class="custom-control-label">Köp i förmånsportal</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Intern Kommentar</label>
                                        <div class="col-lg-10">
                                            <textarea-with-validation
                                                vid="CardOrderComment"
                                                name="Intern Kommentar"
                                                v-model="card_order.CardOrderComment"
                                                :min-height="96"
                                                hide-label
                                            />
                                        </div>
                                    </div>
                                    <div class="form-group row">
                                        <label class="col-form-label col-lg-2">Extern kommentar<div class="text-muted">(följesedel/faktura)</div></label>
                                        <div class="col-lg-10">
                                            <textarea-with-validation
                                                vid="ExternalComment"
                                                name="Extern Kommentar"
                                                v-model="card_order.ExternalComment"
                                                :min-height="96"
                                                hide-label
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div class="card-header bg-light border-top">
                                    <h6 class="card-title"><i class="icon-cart5 mr-2"></i> <span class="text-semibold">Beställning</span></h6>
                                </div>
                                <div class="card-body">
                                    <div class="card">
                                        <div class="d-flex flex-row border-bottom">
                                            <div style="flex: 0 0 100px;" class="px-1 py-2 font-weight-semibold text-center">Serie</div>
                                            <div style="flex-grow: 1;" class="px-1 py-2 font-weight-semibold">Benämning</div>
                                            <div style="flex: 0 0 80px;" class="px-1 py-2 font-weight-semibold text-center">Antal</div>
                                            <div style="flex: 0 0 60px;" class="px-1 py-2 font-weight-semibold text-center">Moms</div>
                                            <div style="flex: 0 0 110px;" class="px-1 py-2 font-weight-semibold text-center">Pris exkl. moms</div>
                                            <div style="flex: 0 0 110px;" class="px-1 py-2 font-weight-semibold text-center">Summa</div>
                                        </div>
                                        <div v-for="(item, itm_idx) in card_order.items" :key="'item-' + itm_idx" class="border-bottom"> <!--  style="border-bottom: 5px solid #ddd;" -->
                                            <div class="d-flex flex-row">
                                                <div style="flex: 0 0 100px;" class="p-1">
                                                    <ValidationProvider :vid="'items.' + itm_idx + '.CardSeriesID'" name="Serie" rules="" v-slot="{ errors }">
                                                        <input
                                                            type="text"
                                                            class="form-control"
                                                            :value="item.CardSeriesID"
                                                            @input="updateItemSeries($event, itm_idx)"
                                                            :class="{'border-danger':errors[0]}"
                                                            :readonly="card_order.PaymentStatusID >= 500"
                                                        >
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                                <div style="flex-grow: 1;" class="p-1">
                                                    <div v-if="item.OrderItemID > 0 && ['6','12','25','30','31','32','1','191'].includes(item.CardSeriesID)">
                                                        <input type="text" class="form-control" v-model="item.CardOrderLine" :readonly="card_order.PaymentStatusID >= 500">
                                                    </div>
                                                    <div v-else-if="item.OrderItemID > 0 && !['6','12','25','30','31','32','1','191'].includes(item.CardSeriesID)" class="d-flex">
                                                        <div style="flex-grow: 1;">
                                                            <input type="text" class="form-control" :value="(item.ProdName || item.SeriesName)" readonly>
                                                        </div>
                                                        <div v-if="item.is_codes_product" class="pl-2">
                                                            <button type="button" class="btn btn-light" @click="showCodes(item)"><i class="icon-ticket"></i></button>
                                                        </div>
                                                    </div>
                                                    <v-select
                                                        v-else-if="String(item.CardSeriesID).charAt(0).toLowerCase() === 'p' || String(item.CardSeriesID).charAt(0).toLowerCase() === 'P'"
                                                        :options="select_products"
                                                        :get-option-label="(prod) => 'P' + prod.ProdID + ' - ' + prod.ProdName"
                                                        placeholder="Sök produkt..."
                                                        @input="inputItemProd($event, itm_idx)"
                                                        :filterable="false"
                                                        @search="searchProducts"
                                                        :disabled="card_order.PaymentStatusID >= 500"
                                                    ></v-select>
                                                    <v-select
                                                        v-else
                                                        :options="card_series"
                                                        :get-option-label="(prod) => prod.CardSeriesID + ': ' + prod.SeriesName"
                                                        placeholder="Sök korttyp..."
                                                        @input="inputItemSerie($event, itm_idx)"
                                                        :disabled="card_order.PaymentStatusID >= 500"
                                                    ></v-select>
                                                </div>
                                                <div style="flex: 0 0 80px;" class="p-1">
                                                    <ValidationProvider :vid="'items.' + itm_idx + '.CardAmount'" name="Antal" rules="" v-slot="{ errors }">
                                                        <input type="number" step="1" class="form-control text-right" v-model="item.CardAmount"
                                                               :class="{'border-danger':errors[0]}" :readonly="card_order.PaymentStatusID >= 500"
                                                               @input="checkTryCredit($event)"
                                                        >
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                                <div style="flex: 0 0 60px;" class="p-1">
                                                    <ValidationProvider :vid="'items.' + itm_idx + '.VAT'" name="Moms" rules="" v-slot="{ errors }">
                                                        <input type="number" step="1" class="form-control text-right" v-model="item.VAT"
                                                               :class="{'border-danger':errors[0], 'bg-yellow-100': item.VAT === 0 }" readonly>
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                                <div style="flex: 0 0 110px;" class="p-1">
                                                    <ValidationProvider :vid="'items.' + itm_idx + '.CardPrice'" name="Pris" rules="" v-slot="{ errors }">
                                                        <input type="number" step="0.01" class="form-control text-right" v-model="item.CardPrice"
                                                               :class="{'border-danger':errors[0], 'bg-yellow-100': item.VAT === 0 }" :readonly="card_order.PaymentStatusID >= 500"
                                                               @input="checkTryCredit($event)"
                                                        >
                                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                    </ValidationProvider>
                                                </div>
                                                <div style="flex: 0 0 110px;" class="p-1">
                                                    <input
                                                        type="text"
                                                        class="form-control text-right"
                                                        :value="new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(item.CardAmount * item.CardPrice).replace(/\s/g, '')"
                                                        readonly
                                                    >
                                                </div>
                                            </div>
                                            <div v-if="item.OrderItemID > 0 && item.CardOrderLine !== '[PRODUCT]' && item.CardSeriesID > 100" class="px-1">
                                                <div class="row" v-if="Object.hasOwn(item_flags, item.CardSeriesID)">
                                                    <div class="col-form-label col-lg-2">
                                                        Produktsortiment:
                                                        <i class="icon-question3 ml-1" :id="'pop-range-1-' + itm_idx"></i>
                                                        <b-popover :key="'pop-range-1-' + itm_idx" :target="'pop-range-1-' + itm_idx" triggers="hover focus">
                                                            <div class="mb-2"><span class="font-weight-semibold">Standardsortiment</span> = Produktutbudet som är valt på kortserien.</div>
                                                            <div class="mb-2"><span class="font-weight-semibold">Överskottsprodukter</span> = Inkludera produkter som är markerade som "visas på alla kort", ett urval görs
                                                                baserat på produktens inköpspris och kortets försäljningspris för att inte visa för billiga eller för dyra produkter.</div>
                                                            <div><span class="font-weight-semibold">Eget urval</span> = Produktutbud specifikt för denna order, produkter kan både läggas till eller tas bort.</div>
                                                        </b-popover>
                                                    </div>
                                                    <div class="col-lg-10 pb-1">
                                                        <div class="alert alert-danger" v-if="item.CardSeriesID in item_valid_range && item_valid_range[item.CardSeriesID] === false">
                                                            Utbudet innehåller produkter med fel momssats!!
                                                        </div>
                                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                            <input :id="'chk-item-flag-' + itm_idx + '-1'" type="checkbox" class="custom-control-input" v-model="item_flags[item.CardSeriesID]" :value="1">
                                                            <label :for="'chk-item-flag-' + itm_idx + '-1'" class="custom-control-label">Standardsortiment</label>
                                                        </div>
<!--                                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">-->
<!--                                                            <input :id="'chk-item-flag-' + itm_idx + '-2'" type="checkbox" class="custom-control-input" v-model="item_flags[item.CardSeriesID]" :value="2">-->
<!--                                                            <label :for="'chk-item-flag-' + itm_idx + '-2'" class="custom-control-label">Xtra Webb.</label>-->
<!--                                                        </div>-->
                                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                            <input :id="'chk-item-flag-' + itm_idx + '-4'" type="checkbox" class="custom-control-input" v-model="item_flags[item.CardSeriesID]" :value="4">
                                                            <label :for="'chk-item-flag-' + itm_idx + '-4'" class="custom-control-label">Överskottsprodukter</label>
                                                        </div>
                                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                            <input :id="'chk-item-flag-' + itm_idx + '-8'" type="checkbox" class="custom-control-input" v-model="item_flags[item.CardSeriesID]" :value="8">
                                                            <label :for="'chk-item-flag-' + itm_idx + '-8'" class="custom-control-label">Eget urval</label>
                                                            <a :href="'/product_range/' + item.CardSeriesID + '/' + card_order.OrderID" class="ml-2" target="_blank">Redigera urval</a>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div class="row">
                                                    <div class="col-form-label col-lg-2"></div>
                                                    <div class="col-lg-10 pb-1">
                                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                                            <input :id="'chk-item-' + itm_idx + '-show_products_hidden_from_all_cards_on_tot'" type="checkbox" class="custom-control-input"
                                                                   v-model="item.options.show_products_hidden_from_all_cards_on_tot">
                                                            <label :for="'chk-item-' + itm_idx + '-show_products_hidden_from_all_cards_on_tot'" class="custom-control-label">Visa "gömd från alla kort"-produkter på TOT</label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div v-if="Object.hasOwn(item_voucher, item.CardSeriesID)" class="row pt-1">
                                                    <div class="col-form-label col-lg-2">Voucher typ:
                                                        <i class="icon-question3 ml-1" :id="'pop-voucher-1-' + itm_idx"></i>
                                                        <b-popover :key="'pop-voucher-1-' + itm_idx" :target="'pop-voucher-1-' + itm_idx" triggers="hover focus">
                                                            <div class="mb-2">Enfunktionsvouchers kräver att kortets produktsortiment endast innehåller produkter med en och samma momssats. Korten faktureras med moms specificerat på fakturan.</div>
                                                            <div>Flerfunktionsvouchers får innehålla ett produktsortiment där produkterna har olika momssatser. Korten faktureras UTAN moms, kom ihåg att ta hänsyn till det vid prissättning!</div>
                                                        </b-popover>
                                                    </div>
                                                    <div class="col pb-1" style="max-width: 400px;">
                                                        <ValidationProvider :vid="'item_voucher.' + item.CardSeriesID + '.voucher_type'" name="Voucher typ" rules="" v-slot="{ errors }">
                                                            <div class="custom-control custom-radio custom-control-inline mt-2">
                                                                <input :id="'chk-item-voucher-' + itm_idx + '-1'" type="radio" class="custom-control-input" v-model="item_voucher[item.CardSeriesID].voucher_type" :value="1">
                                                                <label :for="'chk-item-voucher-' + itm_idx + '-1'" class="custom-control-label">Enfunktionsvoucher</label>
                                                            </div>
                                                            <div class="custom-control custom-radio custom-control-inline mt-2">
                                                                <input :id="'chk-item-voucher-' + itm_idx + '-2'" type="radio" class="custom-control-input" v-model="item_voucher[item.CardSeriesID].voucher_type" :value="2">
                                                                <label :for="'chk-item-voucher-' + itm_idx + '-2'" class="custom-control-label">Flerfunktionsvoucher</label>
                                                            </div>
                                                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                        </ValidationProvider>
                                                    </div>
                                                    <div class="col pb-1" style="max-width: 250px;">
                                                        <ValidationProvider :vid="'item_voucher.' + item.CardSeriesID + '.voucher_vat'" name="Voucher moms" rules="" v-slot="{ errors }">
                                                            <div v-if="item_voucher[item.CardSeriesID].voucher_type === 1">
                                                                <v-select
                                                                    :options="[
                                                                        { id: 6, label: '6% moms (t.ex. böcker)' },
                                                                        { id: 12, label: '12% moms (t.ex. mat)' },
                                                                        { id: 25, label: '25% moms' },
                                                                    ]"
                                                                    :reduce="(opt) => opt.id"
                                                                    v-model="item_voucher[item.CardSeriesID].voucher_vat"
                                                                    @option:selected="updateItemsVat(item.CardSeriesID, $event.id)"
                                                                    :clearable="false"
                                                                ></v-select>
                                                            </div>
                                                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                        </ValidationProvider>
                                                    </div>
                                                </div>
                                                <div v-if="item.IsPoints" class="form-group row">
                                                    <label class="col-form-label col-lg-2">Poäng</label>
                                                    <div class="col-lg-4">
                                                        <ValidationProvider :vid="'items.' + itm_idx + '.no_of_points'" name="Poäng" rules="required" v-slot="{ errors }">
                                                            <input type="number" step="1" class="form-control" v-model="item.no_of_points" v-bind:class="{'border-danger':errors[0]}" :readonly="card_order.PaymentStatusID >= 500">
                                                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                                        </ValidationProvider>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="p-1 border-bottom text-right font-size-lg font-weight-bold">
                                            Totalt exkl. moms: {{ formatCurrency(order_total, 2, (customer.currency ? customer.currency.CurrencyShort : 'SEK')) }}
                                        </div>
                                        <div class="p-1 border-bottom text-right font-size-lg font-weight-bold">
                                            Totalt inkl. moms: {{ formatCurrency(order_total_inc_vat, 2, (customer.currency ? customer.currency.CurrencyShort : 'SEK')) }}
                                        </div>
                                        <div v-if="card_order.PaymentStatusID < 500" class="p-1">
                                            <button type="button" class="btn btn-light btn-block" @click="addOrderItem"><i class="icon-plus3 mr-2"></i>Lägg till rad</button>
                                        </div>
                                    </div>

                                    <div v-if="num_cards_text" v-html="num_cards_text" class="alert alert-info alert-styled-left mt-10"></div>

                                    <div v-if="card_order.OrderID > 0">
                                        <router-link
                                            :to="{ name: 'cardorders.activate', params: { id: card_order.OrderID } }"
                                            class="btn btn-purple btn-sm mr-3"
                                        >
                                            <i class="icon-power2 mr-2"></i>Aktivera/Spärra kort
                                        </router-link>
                                        <router-link :to="{ name: 'stats.average-prices', query: { orderid: String(card_order.OrderID), show: 'ekonomi', year: 'TUTTI' } }" class="btn btn-indigo btn-sm mr-3"><i class="icon-coins mr-2"></i>Ekonomi</router-link>
                                        <router-link
                                            v-if="show_ecard_list_button"
                                            :to="{ name: 'cardorders.ecards', params: { id: card_order.OrderID } }"
                                            class="btn btn-warning btn-sm mr-3"
                                        >
                                            <i class="icon-mention mr-2"></i>Epostlista
                                        </router-link>
                                    </div>
                                </div>
                                <div class="card-footer">
                                    <div class="d-flex justify-content-end">
                                        <limitless-button v-if="hasRole('super-admin')" ref="btncopyorder" class="mr-auto" btn-class="btn-light" type="button" @click="copyCardOrder" :busy="busy_saving" icon="icon-copy3">Kopiera</limitless-button>
                                        <limitless-button ref="btnsaveorder" type="button" @click="saveCardOrder" :busy="busy_saving" icon="icon-floppy-disk">SPARA</limitless-button>
                                    </div>
                                </div>
                            </div> <!-- /card -->
                        </ValidationObserver>
                    </form>

                    <div class="card">
                        <div class="card-header bg-light header-elements-inline">
                            <h6 class="card-title">
                                <i class="icon-list3 mr-2"></i>Kort
                            </h6>
                            <div class="header-elements">
                                <limitless-button v-if="!show_card_info" @click="getCardInfo" btn-class="btn-light" :busy="busy_loading_card_info">Visa</limitless-button>
                            </div>
                        </div>
                        <div v-if="show_card_info" class="card-body">
                            <div v-if="card_info.length > 0" class="">
                                <table class="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Kort</th>
                                            <th>Skapad</th>
                                            <th>Aktiverad</th>
                                            <th>Inlöst</th>
                                            <th>Retur</th>
                                            <th>Inl.</th>
                                            <th>Frakt</th>
                                            <th class="text-right">Kostnad</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr
                                            v-for="(card, c_idx) in card_info"
                                            :key="'card-' + c_idx"
                                            :class="{ 'text-line-through': (card.isBlocked > 0 || card.isReturned > 0), 'bg-orange-100': card.isReturned > 0, 'bg-danger-100': card.isBlocked > 0 }"
                                        >
                                            <td><router-link :to="{ name: 'prodorders.list', query: { search: card.CardUID } }">{{ card.CardUID }}</router-link></td>
                                            <td><nobr>{{ card.CardCreatedTS }}</nobr></td>
                                            <td>{{ (card.CardActivatedTS ? card.CardActivatedTS : '---') }}</td>
                                            <td>{{ (card.CardUsedTS ? card.CardUsedTS : '---') }}</td>
                                            <td>
                                                <span v-if="card.isReturned == 10">Kuverterad, oklistrad</span>
                                                <span v-else-if="card.isReturned == 11">Kuverterad, klistrad</span>
                                                <span v-else-if="card.isReturned == 12">Plano</span>
                                                <span v-else-if="card.isReturned == 13">Vikt</span>
                                                <span v-else>---</span>
                                            </td>
                                            <td>
                                                <span v-if="card.Source == 'scan' || card.Source == 'epp'">SCAN</span>
                                                <span v-else-if="card.Source == 'hemköp'">HEMKÖP</span>
                                                <span v-else-if="card.Source == 'online'">NÄT</span>
                                                <span v-else><i>{{ card.Source }}</i></span>
                                            </td>
                                            <td>{{ card.DeliveryType }}</td>
                                            <td class="text-right">
                                                {{ (6 + 4 + (card.Source == 'epp' ? 8 : 0) + parseInt(card.Price || 0)) }} kr<!-- kort 6kr. kuvert 4 kr -->
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else>
                                <i>Ingen data</i>
                            </div>
                        </div>
                    </div> <!-- /card -->

                    <div class="card">
                        <div class="card-header bg-light header-elements-inline">
                            <h6 class="card-title">
                                <i class="icon-cube3 mr-2"></i>Produkter inlösta kort
                            </h6>
                            <div class="header-elements">
                                <limitless-button v-if="!show_product_info" @click="getProductInfo" btn-class="btn-light" :busy="busy_loading_product_info">Visa</limitless-button>
                            </div>
                        </div>
                        <div v-if="show_product_info" class="card-body">
                            <div v-if="product_info.length > 0">
                                <table class="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>Antal</th>
                                            <th class="text-right">Produkt</th>
                                            <th>Benämning</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(prod, p_idx) in product_info" :key="'product-' + p_idx">
                                            <td><a :href="'/batch/list?type=prod_order&card_order_id=' + card_order.OrderID + '&prod_id=' + prod.ProdID">{{ prod.ProdCount }}</a></td>
                                            <td class="text-right">{{ prod.ProdID }}</td>
                                            <td>{{ prod.ProdName }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div v-else>
                                <i>Ingen data</i>
                            </div>
                        </div>
                    </div> <!-- /card -->

                    <a :href="'/cardorders/' + card_order.OrderID + '/cardstatsexcel'" class="btn btn-light"><i class="icon-file-spreadsheet mr-1"></i>Exportera kortstatistik</a>
                </div> <!-- /col -->
                <div class="col-lg-4">
                    <div class="card">
                        <div class="card-header bg-light">
                            <h6 class="card-title"><i class="icon-mail-read mr-2"></i> <span class="text-semibold">Orderunderlag från ev. mail</span></h6>
                        </div>
                        <div class="card-body">
                            <textarea class="form-control" style="height: 500px;" v-model="card_order.ReceivedOrderInfo"></textarea>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-header bg-light">
                            <h6 class="card-title"><i class="icon-clipboard3 mr-2"></i> <span class="text-semibold">Kopplade ordrar</span></h6>
                        </div>
                        <div class="card-body pb-0">
                            <div v-show="card_order.child_orders && card_order.child_orders.length > 0" class="form-group">
                                <div
                                    v-for="child in card_order.child_orders"
                                    :key="'o-' + child.OrderID"
                                >
                                    <a
                                        :href="'/cardorders/' + child.OrderID"
                                        class="btn btn-link"
                                        target="_blank"
                                    >
                                        #{{ child.OrderID }}
                                    </a>
                                </div>
                            </div>
                            <form v-show="card_order.parent_order_id || (!card_order.child_orders || card_order.child_orders.length === 0)" @submit.prevent="saveCardOrder">
                                <ValidationObserver ref="connectedordersform">
                                    <ValidationProvider tag="div" class="form-group" vid="card_order.parent_order_id" name="Huvudorder" rules="" v-slot="{ errors }">
                                        <label>Huvudorder</label>
                                        <div class="input-group">
                                            <input type="text" class="form-control" v-model="card_order.parent_order_id">
                                            <div class="input-group-append">
                                                <a
                                                    :href="'/cardorders/' + card_order.parent_order_id"
                                                    class="btn btn-light"
                                                    target="_blank"
                                                >
                                                    Gå till
                                                </a>
                                            </div>
                                        </div>
                                        <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                    </ValidationProvider>
                                </ValidationObserver>
                            </form>
                        </div>
                        <div class="card-footer" v-show="card_order.parent_order_id || (!card_order.child_orders || card_order.child_orders.length === 0)">
                            <div class="d-flex justify-content-end">
                                <limitless-button ref="btnsaveconnectedorders" type="button" @click="saveCardOrder" :busy="busy_saving" icon="icon-floppy-disk">SPARA</limitless-button>
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-header bg-light">
                            <h6 class="card-title"><i class="icon-user-tie mr-2"></i> <span class="text-semibold">Säljare</span></h6>
                        </div>
                        <div class="card-body pb-0">
                            <form @submit.prevent="saveCardOrder">
                                <ValidationObserver ref="sellerform">
                                    <select-with-validation
                                        vid="SellerID"
                                        label="Säljare"
                                        v-model="card_order.SellerID"
                                        :reduce="(opt) => opt.id"
                                        component="seller-filter"
                                        placeholder="Ingen säljare vald"
                                        :disabled="card_order.special_commission_approved_by > 0"
                                    />

                                    <div v-if="hasRole('red_sales_crm_user') || hasRole('super-admin')">
                                        <input-with-validation
                                            vid="card_order.comission_special_order"
                                            label="Provision order"
                                            v-model="card_order.comission_special_order"
                                            :disabled="card_order.special_commission_approved_by > 0"
                                        >
                                            <template #input-group-append>
                                                <span class="input-group-text">kr</span>
                                            </template>
                                        </input-with-validation>

                                        <input-with-validation
                                            vid="card_order.comission_special_card"
                                            label="Provision kort"
                                            v-model="card_order.comission_special_card"
                                            :disabled="card_order.special_commission_approved_by > 0"
                                        >
                                            <template #input-group-append>
                                                <span class="input-group-text">kr</span>
                                            </template>
                                        </input-with-validation>
                                    </div>

                                    <ValidationProvider v-if="hasRole('super-admin')" tag="div" class="form-group" vid="card_order.special_commission_approved" name="Provision godkänd" rules="" v-slot="{ errors }">
                                        <div class="custom-control custom-checkbox custom-control-inline mt-2">
                                            <input id="chk-special_commission_approved" type="checkbox" class="custom-control-input" v-model="card_order.special_commission_approved" :true-value="1" :false-value="0">
                                            <label for="chk-special_commission_approved" class="custom-control-label">Provision godkänd</label>
                                        </div>
                                        <span v-if="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                        <span v-else-if="card_order.special_commission_approved_by > 0 && special_commission_approved_by_user" class="text-muted">
                                            // {{ special_commission_approved_by_user.first_name }} {{ special_commission_approved_by_user.last_name }}
                                        </span>
                                    </ValidationProvider>
                                </ValidationObserver>
                            </form>
                        </div>
                        <div class="card-footer">
                            <div class="d-flex justify-content-end">
                                <limitless-button ref="btnsaveseller" type="button" @click="saveCardOrder" :busy="busy_saving" icon="icon-floppy-disk">SPARA</limitless-button>
                            </div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-header bg-light">
                            <h6 class="card-title"><i class="icon-price-tag mr-2"></i> <span class="text-semibold">Specialpriser</span></h6>
                        </div>
                        <div>
                            <table v-if="price_lists.length > 0" class="table table-xs table-striped border-bottom">
                                <thead>
                                    <tr>
                                        <th>Kortserie</th>
                                        <th>Giltig</th>
                                        <th>Pris</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(pl, pl_idx) in price_lists" :key="'pl-' + pl_idx">
                                        <td>{{ pl.CardSeriesID }} {{ pl.SeriesName }}</td>
                                        <td>{{ pl.ValidFrom }} - {{ pl.ValidTo }}</td>
                                        <td>{{ pl.myPrice }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <div v-else class="card-body pb-0">
                                <i>Inga specialpriser</i>
                            </div>
                        </div>
                    </div>

                    <file-list-uploader
                        ref="orderFiles"
                        recursive
                        class="mb-3"
                        model-type="cardorders"
                        :model-id="id"
                        :extensions="['jpg','jpeg','png','pdf','mp4','doc','docx','xls','xlsx']"
                        :accept="'image/png,image/jpeg,application/pdf,video/mp4,' +
                            'application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
                            'application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'"
                        multiple
                        auto-start-upload
                        display-list
                        header="Filer/dokument"
                    ></file-list-uploader>

                    <div class="card">
                        <div class="card-header bg-light">
                            <h6 class="card-title"><i class="icon-clipboard3 mr-2"></i> <span class="text-semibold">Kundnotering</span></h6>
                        </div>
                        <div class="card-body">
                            <div v-html="internal_note" style="color: #990000;"></div>
                        </div>
                    </div>

                    <div class="card">
                        <div class="card-header bg-light">
                            <h6 class="card-title"><i class="icon-history mr-2"></i> <span class="text-semibold">Logg</span></h6>
                        </div>
                        <div class="d-flex flex-column">
                            <div
                                v-for="(log, l_idx) in card_order_log"
                                :key="'log-' + l_idx"
                                class="border-bottom p-2"
                            >
                                <div>
                                    <span class="font-weight-semibold">{{ (log.user ? log.user.FullName : 'Okänd') }}</span>
                                    <span class="text-muted">
                                        <span class="mx-1">&bull;</span>
                                        {{ formatDateTime(log.LogTS) }}
                                    </span>
                                    <span v-if="log.CommentType" class="text-muted">
                                        <span class="mx-1">&bull;</span>
                                        {{ log.CommentType }}
                                    </span>
                                    <template v-if="log.data">
                                        <span v-if="log.data.delivery_channel === 'sms'" class="badge badge-light ml-1" :title="'SMS-id: ' + (log.data.sent_id || 'saknas')">SMS</span>
                                        <span v-if="log.data.delivered_ts || log.data.opened_ts || log.data.clicked_ts || log.data.bounced_ts" class="mx-1 text-muted">&bull;</span>
                                        <span v-if="log.data.delivered_ts">
                                            <i :id="'delivered_ts-' + log.LogID" :class="log.data.delivery_channel === 'sms' ? 'icon-mobile text-success' : 'icon-mail5 text-success'"></i>
                                            <b-popover key="warning-delivered_ts" :target="'delivered_ts-' + log.LogID" triggers="hover focus" placement="bottom">
                                                Levererat: {{ formatDateTime(log.data.delivered_ts) }}
                                            </b-popover>
                                        </span>
                                        <span v-if="log.data.delivery_channel !== 'sms' && log.data.opened_ts">
                                            <i :id="'opened_ts-' + log.LogID" class="icon-mail-read text-success"></i>
                                            <b-popover key="warning-opened_ts" :target="'opened_ts-' + log.LogID" triggers="hover focus" placement="bottom">
                                                Öppnat: {{ formatDateTime(log.data.opened_ts) }}
                                            </b-popover>
                                        </span>
                                        <span v-if="log.data.delivery_channel !== 'sms' && log.data.clicked_ts">
                                            <i :id="'clicked_ts-' + log.LogID" class="icon-touch text-success"></i>
                                            <b-popover key="warning-clicked_ts" :target="'clicked_ts-' + log.LogID" triggers="hover focus" placement="bottom">
                                                Klickat: {{ formatDateTime(log.data.clicked_ts) }}
                                            </b-popover>
                                        </span>
                                        <span v-if="log.data.bounced_ts">
                                            <i :id="'bounced_ts-' + log.LogID" class="icon-cross2 text-danger"></i>
                                            <b-popover key="warning-bounced_ts" :target="'bounced_ts-' + log.LogID" triggers="hover focus" placement="bottom">
                                                <div>Ej levererat: {{ formatDateTime(log.data.bounced_ts) }}</div>
                                                <div>{{ log.data.bounced_msg }}</div>
                                            </b-popover>
                                        </span>
                                    </template>
                                </div>
                                <div v-html="nl2br(log.LogAction)"></div>
<!--                                <div style="font-size: 9px; color: #999999; font-style: italic;">{{ formatDateTime(log.LogTS) }} / {{ (log.user ? log.user.FullName : 'Okänd') }}</div>-->
<!--                                <div style="padding-left: 10px;" v-html="log.LogAction.replaceAll('\n', '<br>')"></div>-->
                            </div>
                            <div v-if="!show_all_logs && card_order.log.length>0" class="text-center pt-3 pb-3">
                                <button type="button" class="btn btn-light" @click="show_all_logs=true"><i class="icon-arrow-down16 mr-2 text-muted"></i>Visa mer<i class="icon-arrow-down16 ml-2 text-muted"></i></button>
                            </div>
                        </div>
                    </div>
                </div> <!-- /col -->
            </div> <!-- /row -->

            <b-modal id="modal-contact-edit" @hidden="edit_contact_id = null" size="lg" hide-footer>
                <customer-contact-edit :id="edit_contact_id" :customer="customer" include @saved="selectContact($event)" @deleted="selectContact()"></customer-contact-edit>
            </b-modal>

            <b-modal title="Produktkoder" id="modal-show-codes" ok-title="Stäng" ok-variant="light" ok-only>
                <div v-if="codes_item">
                    <div v-for="code in codes_item.codes" :key="'code-' + code.code">
                        {{ code.code }}<span v-if="code.expire_date"> - Giltig till {{ code.expire_date }}</span>
                    </div>
                </div>
            </b-modal>

            <b-modal title="Redigera orderdatum" id="modal-edit-date" @ok="saveCardOrder" ok-variant="primary" ok-title="Spara" cancel-title="Avbryt" cancel-variant="light">
                <div class="form-group">
                    <label>Skapad</label>
                    <v-date-picker
                        v-model="card_order.CreatedTS"
                        mode="dateTime"
                        :model-config="{ type: 'string', mask: 'iso' }"
                        :locale="{ id: 'sv', firstDayOfWeek: 2, masks: { weekdays: 'WW', input: 'YYYY-MM-DD HH:mm', inputDateTime24hr: 'YYYY-MM-DD HH:mm' } }"
                        show-iso-weeknumbers
                        is24hr
                    >
                        <template v-slot="{ inputValue, togglePopover }">
                            <input class="form-control" :value="inputValue" @focus="togglePopover()">
                        </template>
                    </v-date-picker>
                </div>
            </b-modal>

            <b-modal id="modal-register-payment" size="lg" title="Registrera betalning" @ok="registerPayment">
                <ValidationObserver ref="paymentform">
                    <div class="form-group row">
                        <label class="col-form-label col-lg-2">Ordernummer</label>
                        <div class="col-lg-10">
                            <ValidationProvider vid="order_id" name="Ordernummer" v-slot="{ errors }">
                                <input type="text" class="form-control" :value="payment.order_id" disabled>
                                <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-form-label col-lg-2">Datum</label>
                        <div class="col-lg-10">
                            <ValidationProvider vid="payment_date" name="Datum" rules="required" v-slot="{ errors }">
                                <v-date-picker v-model="payment.payment_date" mode="date" :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }" :masks="{ input: 'YYYY-MM-DD' }" show-iso-weeknumbers>
                                    <template v-slot="{ inputValue, togglePopover }">
                                        <input class="form-control" :value="inputValue" @focus="togglePopover()">
                                    </template>
                                </v-date-picker>
                                <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-form-label col-lg-2">Referens / Kvittonummer</label>
                        <div class="col-lg-10">
                            <ValidationProvider vid="reference" name="Referens" v-slot="{ errors }">
                                <input type="text" class="form-control" v-model="payment.reference">
                                <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-form-label col-lg-2">Belopp</label>
                        <div class="col-lg-10">
                            <ValidationProvider vid="amount" name="Belopp" rules="required" v-slot="{ errors }">
                                <input type="text" class="form-control" v-model="payment.amount">
                                <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                <span>Kvar att betala: {{ formatCurrency(left_to_pay, 2, (customer.currency ? customer.currency.CurrencyShort : 'SEK')) }}</span>
                            </ValidationProvider>
                        </div>
                    </div>
                    <div class="form-group row">
                        <label class="col-form-label col-lg-2">Kommentar</label>
                        <div class="col-lg-10">
                            <ValidationProvider vid="comment" name="Kommentar" v-slot="{ errors }">
                                <textarea rows="5" class="form-control" placeholder="Kommentar" v-model="payment.comment"></textarea>
                                <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                            </ValidationProvider>
                        </div>
                    </div>
                </ValidationObserver>
                <template #modal-footer="{ cancel, ok }">
                    <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                    <limitless-button ref="btnregisterpayment" type="button" @click="ok">Registrera</limitless-button>
                </template>
            </b-modal>

            <activity-notification v-if="card_order && card_order.OrderID > 0" :channel="'card-order-' + card_order.OrderID"></activity-notification>
        </div>
        <div class="content" v-else-if="not_found">
            <i>Beställningen hittades inte</i>
        </div>
        <div class="content" v-else>
            <i>Något gick fel...</i>
        </div>

        <b-modal
            id="edit_delivery_address"
            title="Redigera leveransadress"
            key="edit_delivery_address"
            size="xl"
            @ok="saveDeliveryAddress"
        >
            <ValidationObserver
                ref="deliveryaddressform"
                v-if="delivery_address"
            >
                <div class="form-group row mb-2">
                    <label class="col-form-label col-lg-2"></label>
                    <div class="col-lg-8">
                        <div class="input-group">
                            <div class="input-group-prepend"><span class="input-group-text">Snabbval:</span></div>
                            <v-select
                                :options="select_delivery_addresses"
                                :placeholder="'Välj leveransaddress'"
                                @option:selected="fillDeliveryAddress"
                            ></v-select>
                        </div>
                    </div>
                    <div class="col-lg-2">
                        <router-link :to="{ name: 'customers.edit', params: { id: card_order.CustomerID } }" class="btn btn-block btn-secondary" target="_blank">Lägg till ny</router-link>
                    </div>
                </div>
                <div class="row">
                    <label class="col-form-label col-lg-2">Företag</label>
                    <div class="col-lg-10">
                        <input-with-validation
                            vid="name"
                            label="Företag"
                            v-model="delivery_address.name"
                            compact
                            hide-label
                        />
                    </div>
                </div>
                <div class="row">
                    <label class="col-form-label col-lg-2">C/O</label>
                    <div class="col-lg-10">
                        <input-with-validation
                            vid="co"
                            label="C/O"
                            v-model="delivery_address.co"
                            compact
                            hide-label
                        />
                    </div>
                </div>
                <div class="row">
                    <label class="col-form-label col-lg-2">Gata 1</label>
                    <div class="col-lg-10">
                        <input-with-validation
                            vid="street_1"
                            label="Gata 1"
                            v-model="delivery_address.street_1"
                            compact
                            hide-label
                        />
                    </div>
                </div>
                <div class="row">
                    <label class="col-form-label col-lg-2">Gata 2</label>
                    <div class="col-lg-10">
                        <input-with-validation
                            vid="street_2"
                            label="Gata 2"
                            v-model="delivery_address.street_2"
                            compact
                            hide-label
                        />
                    </div>
                </div>
                <div class="row">
                    <label class="col-form-label col-lg-2">Postort</label>
                    <div class="col-lg-2">
                        <input-with-validation
                            vid="postal_code"
                            label="Postnummer"
                            v-model="delivery_address.postal_code"
                            compact
                            hide-label
                        />
                    </div>
                    <div class="col-lg-5">
                        <input-with-validation
                            vid="city"
                            label="Postort"
                            v-model="delivery_address.city"
                            compact
                            hide-label
                        />
                    </div>
                    <div
                        class="col-lg-3 col-form-label"
                    >
                        <span style="font-weight: bold; color: #990000;">{{ delivery_info }}</span>
                    </div>
                </div>
                <div class="form-group row mb-3">
                    <label class="col-form-label col-lg-2">Land</label>
                    <div class="col-lg-4">
                        <select-with-validation
                            component="country-filter"
                            vid="country_code"
                            label="Land"
                            v-model="delivery_address.country_code"
                            :reduce="c => c.id"
                            placeholder="Välj land"
                            hide-label
                        ></select-with-validation>
                    </div>
                    <div class="col-lg-3">
                        <input-with-validation
                            vid="region"
                            label="Region/Stat"
                            v-model="delivery_address.region"
                            placeholder="Region/Stat"
                            hide-label
                        ></input-with-validation>
                    </div>
                </div>

                <div class="form-group row mb-3">
                    <label class="col-form-label col-lg-2">Kontaktperson</label>
                    <div class="col-lg-4">
                        <input-with-validation
                            vid="contact_name"
                            label="Kontaktperson"
                            v-model="delivery_address.contact_name"
                            hide-label
                        />
                    </div>
                    <div class="col-lg-3">
                        <input-with-validation
                            vid="contact_phone"
                            label="Mobiltelefonnummer"
                            v-model="delivery_address.contact_phone"
                            hide-label
                        />
                    </div>
                    <div class="col-lg-3">
                        <input-with-validation
                            vid="contact_email"
                            label="E-postadress"
                            v-model="delivery_address.contact_email"
                            hide-label
                        />
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Fraktsätt</label>
                    <div class="col-lg-6">
                        <select-with-validation
                            vid="service_type"
                            v-model="delivery_address.service_type"
                            :reduce="(opt) => opt.name"
                            component="ntaps-service-type-filter"
                        ></select-with-validation>
                    </div>
                </div>

                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Leveransinstruktion</label>
                    <div class="col-lg-6">
                        <input-with-validation
                            vid="transport_instruction"
                            label="Leveransinstruktion"
                            v-model="delivery_address.transport_instruction"
                            hide-label
                        />
                    </div>
                </div>
                <div class="form-group row">
                    <label class="col-form-label col-lg-2">Packinstruktion</label>
                    <div class="col-lg-6">
                        <input-with-validation
                            vid="pack_instruction"
                            label="Packinstruktion"
                            v-model="delivery_address.pack_instruction"
                            hide-label
                        />
                    </div>
                </div>

                <div class="card">
                    <div class="card-header header-elements-inline py-1">
                        <h6 class="card-title">Plocklista för denna adress</h6>
                        <div class="header-elements">
                            <button
                                type="button"
                                @click="() => {
                                    if (!Array.isArray(delivery_address.items)) {
                                        delivery_address.items = [];
                                    }
                                    delivery_address.items.push({ sku: '', quantity: '' });
                                }"
                                class="btn btn-light"
                            ><i class="icon-plus3 mr-2"></i>Lägg till</button>
                        </div>
                    </div>
                    <table class="table table-sm">
                        <tr>
                            <th>SKU</th>
                            <th>Antal</th>
                        </tr>
                        <tr
                            v-for="(item, i_index) in delivery_address.items"
                            :key="'da-item-' + i_index"
                        >
                            <td>
                                <input-with-validation
                                    type="text"
                                    name="sku"
                                    label="SKU"
                                    v-model="item.sku"
                                    rules="required"
                                    class="mb-0"
                                    hide-label
                                ></input-with-validation>
                            </td>
                            <td>
                                <input-with-validation
                                    type="number"
                                    name="quantity"
                                    label="antal"
                                    v-model="item.quantity"
                                    rules="required"
                                    min="1"
                                    step="1"
                                    class="mb-0"
                                    hide-label
                                ></input-with-validation>
                            </td>
                            <td>
                                <button
                                    type="button"
                                    @click="delivery_address.items.splice(i_index1, 1)"
                                    class="btn btn-danger"
                                ><i class="icon-trash"></i></button>
                            </td>
                        </tr>
                    </table>
                </div>
            </ValidationObserver>
            <template #modal-footer="{ cancel, ok }">
                <limitless-button ref="btndeletedeliveryaddress" type="button" @click="deleteDeliveryAddress(delivery_address)" class="mr-auto" :busy="busy_deleting_address" btn-class="btn-danger"
                                  :disabled="ntaps_parcels && ntaps_parcels.filter((p) => p.shipment.internal_reference3 == delivery_address.id).length > 0">Radera</limitless-button>
                <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                <limitless-button ref="btnsavedeliveryaddress" type="button" @click="ok" :busy="busy_saving_address">Spara</limitless-button>
            </template>
        </b-modal>

        <b-modal
            id="modal_upload_address_list"
            title="Ladda upp adresslista"
            key="modal_upload_address_list"
            @ok="uploadAddressList"
        >
            <ValidationObserver ref="uploadaddresslistform">
                <form @submit.prevent="uploadAddressList">
                    <ValidationProvider vid="file" name="Fil" v-slot="{ errors }">
                        <div class="form-group">
                            <limitless-file-input
                                @change="inputFileChanged"
                                ref="addresslistfileinput"
                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                            ></limitless-file-input>
                            <span class="validation-invalid-label">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                    <ValidationProvider vid="replace" name="Ersätt" v-slot="{ errors }">
                        <div class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" name="replace" id="chk-replace" v-model="address_list_replace">
                            <label class="custom-control-label" for="chk-replace">Ersätt befintliga leveransadresser</label>
                        </div>
                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                    </ValidationProvider>
                </form>
            </ValidationObserver>
            <template #modal-footer="{ cancel, ok }">
                <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                <limitless-button ref="btnuploadaddresslist" type="button" @click="ok" :busy="busy_uploading_addresses">Ladda upp</limitless-button>
            </template>
        </b-modal>

        <b-modal
            id="modal_print_small_label"
            title="Skriv ut adresslapp"
            key="modal_print_small_label"
            @ok="printSmallLabel"
        >
            <ValidationObserver ref="printsmalllabelform">
                <form @submit.prevent="printSmallLabel">
                    <ValidationProvider vid="printer_tag" name="Skrivare" rules="required" v-slot="{ errors }">
                        <div class="form-group" :class="{ 'has-error': errors[0] }">
                            <label class="font-weight-semibold" for="small_label_printer_tag">Välj skrivare</label><br>
                            <v-select
                                id="small_label_printer_tag"
                                :options="smalllabel_printers"
                                :reduce="opt => opt.tag"
                                label="name"
                                placeholder="-- Välj skrivare --"
                                v-model="small_label.printer_tag"
                            ></v-select>
                            <span class="validation-invalid-label">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                    <ValidationProvider vid="template" name="template" rules="required" v-slot="{ errors }">
                        <div class="form-group">
                            <label class="d-block">Frankering?</label>
                            <div class="custom-control custom-radio custom-control-inline">
                                <input type="radio" class="custom-control-input" name="template" id="rdo-without-franking" v-model="small_label.template" value="adresslapp_utan_frankering">
                                <label class="custom-control-label" for="rdo-without-franking">UTAN frankering</label>
                            </div>
                            <div class="custom-control custom-radio custom-control-inline">
                                <input type="radio" class="custom-control-input" name="template" id="rdo-with-franking" v-model="small_label.template" value="adresslapp_med_frankering" :disabled="!smalllabel_allow_franking">
                                <label class="custom-control-label" for="rdo-with-franking">MED frankering</label>
                            </div>
                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                    <ValidationProvider vid="template" name="template" rules="required" v-slot="{ errors }">
                        <div class="form-group">
                            <div class="custom-control custom-checkbox custom-control-inline">
                                <input type="checkbox" class="custom-control-input" name="template" id="cht-print-printed" v-model="small_label.print_printed">
                                <label class="custom-control-label" for="cht-print-printed">Skriv ut redan utskrivna</label>
                            </div>
                            <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                        </div>
                    </ValidationProvider>
                </form>
            </ValidationObserver>
            <template #modal-footer="{ cancel, ok }">
                <limitless-button
                    v-if="small_label.card_order_address_id"
                    @click="printSmallLabelPDF(small_label.card_order_address_id)"
                    btn-class="btn-light"
                    class="mr-auto"
                    icon="icon-file-pdf"
                >Visa PDF</limitless-button>
                <button type="button" class="btn btn-light" @click="cancel">Avbryt</button>
                <limitless-button
                    ref="btnprintsmalllabel"
                    type="button"
                    @click="ok"
                    :busy="busy_printing_small_label"
                    icon="icon-printer2"
                >Skriv ut</limitless-button>
            </template>
        </b-modal>

        <b-modal
            id="modal_mail_preview"
            :title="'Skicka ' + mail_type_human"
            key="modal_mail_preview"
            ok-title="Skicka"
            cancel-title="Stäng"
            cancel-variant="light"
            size="xl"
            @ok="sendMail"
            :ok-disabled="busy_sending_mail || !mail.to"
            :busy="busy_sending_mail"
            :no-close-on-backdrop="busy_sending_mail"
            :no-close-on-esc="busy_sending_mail"
            :hide-header-close="busy_sending_mail"
        >
            <div v-if="card_order">
                <ValidationObserver ref="mailform">
                    <div
                        v-if="mail_type === 'delivery' && (this.unifaun_data.filter((row) => !row.isHidden && row.kollino).length <= 0 && this.ntaps_parcels.filter((row) => !row.deleted_at).length <= 0)"
                        class="alert alert-warning"
                    >
                        Det finns inga kollinummer kopplade till denna order, vill du verkligen skicka leveransbesked?
                    </div>
                    <div class="form-group row">
                        <label class="col col-form-label" style="max-width: 150px;">Till:</label>
                        <div class="col" style="max-width: 500px;">
                            <input-with-validation
                                vid="to"
                                rules="required"
                                label="Till"
                                v-model="mail.to"
                                :placeholder="card_order.CustomerContactMail ? card_order.CustomerContactMail : 'E-postadress'"
                                hide-label
                            />
                        </div>
                    </div>
                    <div v-if="mail.to || card_order.CustomerContactMail">
                        <div class="form-group row">
                            <label class="col col-form-label" style="max-width: 150px;">Säljare:</label>
                            <div class="col" style="max-width: 500px;">
                                <select-with-validation
                                    vid="contact_id"
                                    label="Säljare"
                                    :reduce="(opt) => opt.id"
                                    v-model="mail.contact_id"
                                    placeholder="--- Generell kontaktinformation ---"
                                    :clearable="true"
                                    hide-label
                                    component="seller-filter"
                                />
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col col-form-label" style="max-width: 150px;">Meddelande:</label>
                            <div class="col" style="max-width: 500px;">
                                <textarea-with-validation
                                    vid="message"
                                    name="Meddelande"
                                    v-model="mail.message"
                                    hide-label
                                    :strip-tags="false"
                                />
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col col-form-label" style="max-width: 150px;">Bifoga:</label>
                            <div class="col pt-2">
                                <ValidationProvider vid="pdf" name="PDF" v-slot="{ errors }">
                                    <div class="custom-control custom-radio custom-control-inline">
                                        <input type="radio" class="custom-control-input" name="rdo-attach" id="rdo-attach-none" v-model="mail.pdf" :value="null">
                                        <label class="custom-control-label" for="rdo-attach-none">Nej</label>
                                    </div>
                                    <div v-if="mail_type === 'message' || mail_type === 'confirmation'" class="custom-control custom-radio custom-control-inline">
                                        <input type="radio" class="custom-control-input" name="rdo-attach" id="rdo-attach-confirmation" v-model="mail.pdf" :value="'confirmation'">
                                        <label class="custom-control-label" for="rdo-attach-confirmation">BEKRÄFTELSE (PDF)</label>
                                    </div>
                                    <div v-if="mail_type === 'message' || mail_type === 'proforma'" class="custom-control custom-radio custom-control-inline">
                                        <input type="radio" class="custom-control-input" name="rdo-attach" id="rdo-attach-confirmation" v-model="mail.pdf" :value="'proforma'">
                                        <label class="custom-control-label" for="rdo-attach-confirmation">PROFORMA (PDF)</label>
                                    </div>
                                    <div v-if="mail_type === 'message' || mail_type === 'quote'" class="custom-control custom-radio custom-control-inline">
                                        <input type="radio" class="custom-control-input" name="rdo-attach" id="rdo-attach-confirmation" v-model="mail.pdf" :value="'quote'">
                                        <label class="custom-control-label" for="rdo-attach-confirmation">OFFERT (PDF)</label>
                                    </div>
                                    <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="form-group row">
                            <label class="col col-form-label" style="max-width: 150px;">Egna bilagor:</label>
                            <div class="col" style="max-width: 500px;">
                                <ValidationProvider vid="attachments" name="Bilagor" v-slot="{ errors }">
                                    <limitless-file-input
                                        multiple
                                        @change="mailAttachmentsChanged"
                                        ref="mailattachmentinput"
                                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.txt,.csv"
                                    ></limitless-file-input>
                                    <span class="form-text text-muted">Bild, dokument eller PDF. Max 10 filer, totalt 8 MB.</span>
                                    <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                        </div>
                        <div class="form-group row" v-if="mail_type === 'confirmation' && (card_order.hasDigitalDelivery || card_order.hasMailDelivery)">
                            <label class="col col-form-label" style="max-width: 150px;">Inkludera:</label>
                            <div class="col pt-2">
                                <ValidationProvider vid="text_address_list" name="Länk till adresslista-mall" v-slot="{ errors }">
                                    <div class="custom-control custom-checkbox custom-control-inline">
                                        <input id="chk-attach-pdf" type="checkbox" class="custom-control-input" v-model="mail.text_address_list">
                                        <label for="chk-attach-pdf" class="custom-control-label">Länk till adresslista-mall</label>
                                    </div>
                                    <span v-show="errors[0]" class="validation-invalid-label">{{ errors[0] }}</span>
                                </ValidationProvider>
                            </div>
                        </div>
                    </div>
                </ValidationObserver>
                <hr>
                <iframe
                    v-if="mail_preview_url"
                    :src="mail_preview_url"
                    style="border: 0; width: 100%;"
                    :style="{ height: (mail_type=== 'message' ? 1000 : (mail.message ? 2000 : 1700)) + 'px' }"
                ></iframe>
                <hr v-if="pdf_preview_url">
                <iframe
                    v-if="pdf_preview_url"
                    :src="pdf_preview_url"
                    style="border: 0; width: 100%;"
                    :style="{ height: 1200 + 'px' }"
                ></iframe>
            </div>
            <template #modal-footer="{ ok, cancel }">
                <button type="button" class="btn btn-light" :disabled="busy_sending_mail" @click="cancel">Stäng</button>
                <limitless-button ref="btnsendmail" icon="icon-paperplane" :busy="busy_sending_mail" @click="ok" :disabled="!card_order.CustomerContactMail && !mail.to">Skicka</limitless-button>
            </template>
        </b-modal>

        <b-modal
            id="modal_create_print"
            title="Tryckfil och historik"
            key="modal_create_print"
            hide-footer
            :no-close-on-backdrop="production_dialog_busy" :no-close-on-esc="production_dialog_busy" :hide-header-close="production_dialog_busy"
            scrollable
            ok-title="Stäng"
            ok-variant="light"
            size="xl"
        >
            <production-files v-if="card_order" :order-id="id" :state="productionStatus" :initial-tab="production_dialog_tab" @updated="productionFilesUpdated" @busy="production_dialog_busy = $event" @close="$bvModal.hide('modal_create_print')" />
        </b-modal>

        <b-modal
            id="modal_create_korr"
            title="Skapa korr"
            key="modal_create_korr"
            scrollable
            size="xl"
            :no-close-on-backdrop="korr_dialog_state.saving" :no-close-on-esc="korr_dialog_state.saving" :hide-header-close="korr_dialog_state.saving"
        >
            <greeting-proof v-if="card_order" ref="orderProof" :order-id="id" @saved="productionFilesUpdated" @status-change="korr_dialog_state = $event" />
            <template #modal-footer="{ cancel }">
                <button type="button" class="btn btn-light" :disabled="korr_dialog_state.saving" @click="cancel()">Avbryt</button>
                <button type="button" class="btn btn-primary" :disabled="!korr_dialog_state.canSave" @click="$refs.orderProof.save()">
                    {{ korr_dialog_state.saving ? 'Sparar…' : 'Spara kort på order' }}
                </button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import ProductionFiles from '@components/Greeting/ProductionFiles.vue';
import GreetingProof from '@components/Greeting/GreetingProof.vue';
import OrderProductionFlow from '@components/Greeting/OrderProductionFlow.vue';
import { approvalChangeDecision, greetingFields, greetingValues } from '@services/ApprovalChangeDecision';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import LimitlessPageHeader from '@components/Layout/partials/LimitlessPageHeader';
import LimitlessPageHeaderButton from '@components/Layout/partials/LimitlessPageHeaderButton';
import ActivityNotification from '@components/User/ActivityNotification';
import MediabankSelector from '@components/MediabankSelector';
import CustomerContactEdit from '@app/pages/customers/partials/CustomerContactEdit';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import UserRepository from '@services/repositories/UserRepository';
import vSelect from 'vue-select';
import {
    getWeek,
    genMod10,
    openPopupWindow,
    formatDateTime,
    hasRole,
    hasPermission,
    formatCurrency,
    nl2br,
} from '@services/Helpers';
import getError from '@services/ErrorHelper';
import { debounce } from 'lodash';
import Vue from 'vue';
import SelectWithValidation from '@components/Form/SelectWithValidation.vue';
import InputWithValidation from '@components/Form/InputWithValidation.vue';
import TextareaWithValidation from '@components/Form/TextareaWithValidation.vue';
import BackendRepository from '@services/repositories/BackendRepository';
import LimitlessFileInput from '@components/Limitless/LimitlessFileInputComponent.vue';
import Cookies from 'js-cookie';
import PrinterRepository from '@services/repositories/Printer/PrinterRepository';
import ColorPicker from '@components/Form/ColorPicker.vue';
import FileListUploader from '@components/Form/FileListUploader.vue';

export default {
    name: 'CardOrderEdit',
    components: {
        ProductionFiles,
        GreetingProof,
        OrderProductionFlow,
        ColorPicker,
        LimitlessFileInput,
        TextareaWithValidation,
        InputWithValidation,
        SelectWithValidation,
        CustomerContactEdit,
        LimitlessButton,
        LimitlessPageHeader,
        LimitlessPageHeaderButton,
        ActivityNotification,
        MediabankSelector,
        FileListUploader,
        VDatePicker,
        vSelect,
    },
    props: {
        id: {
            type: [Number, String],
            default: 0,
        },
    },
    data: () => ({
        customer_id: null,
        not_found: false,
        initialized: false,
        busy_loading: true,
        busy_saving: false,
        confirming_order_status: false,
        order_status_selector_key: 0,
        busy_create_paylink: false,
        busy_saving_address: false,
        busy_deleting_address: false,
        busy_loading_price_lists: false,
        card_order: null,
        customer: null,
        item_flags: {},
        item_voucher: {},
        item_valid_range: {},
        unifaun_data: [],
        ntaps_parcels: [],
        price_lists: [],
        card_series: [],
        statuses: [],
        delivery_statuses: [],
        languages: [],
        order_flags: [],
        mail_messages: [],
        brands: [],
        order_payments: [],
        payment_links: [],
        delivery_info: '',
        busy_loading_card_info: false,
        show_card_info: false,
        card_info: [],
        busy_loading_product_info: false,
        show_product_info: false,
        product_info: [],
        hide_kolli: [],
        show_all_logs: false,
        select_products: [],
        edit_contact_id: null,
        show_ecard_list_button: false,
        payment: {},
        special_commission_approved_by_user: null,
        busy_sending_mail: false,
        mail_type: null,
        mail_preview_url: '',
        pdf_preview_url: '',
        mail: {
            contact_id: null,
            message: '',
            to: '',
            pdf: null,
            text_address_list: false,
        },
        mail_attachments: [],
        codes_item: null,
        busy_loading_addresses: false,
        delivery_addresses: [],
        delivery_address: null,
        busy_uploading_addresses: false,
        address_list_files: null,
        address_list_replace: false,
        busy_printing_small_label: false,
        small_label: {
            printer_tag: null,
            template: null,
            card_order_id: null,
            card_order_address_id: null,
            print_printed: false,
        },
        smalllabel_printers: [],
        smalllabel_allow_franking: true,
        credit_warning_shown: false,
        production_files: {
            step: 'needs_source',
            valid_print: false,
            revision: 0,
            current: {},
            versions: {},
            approved: null,
        },
        saved_order_greeting: null,
        greeting_image_selected: false,
        production_dialog_tab: 'print',
        production_dialog_busy: false,
        korr_dialog_state: { canSave: false, saving: false },

    }),
    mounted() {
        BackendRepository.getStatuses().then((res) => {
            this.statuses = res;
        });
        BackendRepository.getCardOrderDeliveryStatuses().then((res) => {
            this.delivery_statuses = res;
        });
        BackendRepository.getLanguages().then((res) => {
            this.languages = res;
        });
        BackendRepository.getBranding().then((res) => {
            this.brands = res;
        });
        BackendRepository.getFlags().then((res) => {
            this.order_flags = res;
        });
        apiClient.get('/vue/cardseries?only_series=1&per_page=10000')
            .then((response) => {
                this.card_series = response.data.data;
            });
        this.getPrinters();
        this.init();
    },
    watch: {
        id() {
            this.init();
        },
        'small_label.printer_tag': function smallLabelPrinterTag(val) {
            const printer = this.smalllabel_printers.find((p) => p.tag === val);
            if (printer && printer.media === 'adr_99x49') {
                this.smalllabel_allow_franking = true;
            } else {
                this.smalllabel_allow_franking = false;
                this.small_label.template = 'adresslapp_utan_frankering';
            }
        },
        // eslint-disable-next-line func-names
        'card_order.ContactID': function (val) {
            // eslint-disable-next-line no-param-reassign
            val = parseInt(val, 10);
            this.card_order.CustomerContact = '';
            this.card_order.CustomerContactPhone = '';
            this.card_order.CustomerContactMail = '';

            if (val > 0) {
                const contact = this.customer.contacts.find((cont) => parseInt(cont.ContactID, 10) === val);
                if (contact) {
                    this.card_order.CustomerContact = contact.FullName;
                    if (contact.PhoneLandline) {
                        this.card_order.CustomerContactPhone = contact.PhoneLandline;
                    }
                    if (contact.PhoneMobile) {
                        if (this.card_order.CustomerContactPhone) {
                            this.card_order.CustomerContactPhone += ' / ';
                        }
                        this.card_order.CustomerContactPhone += contact.PhoneMobile;
                    }
                    this.card_order.CustomerContactMail = contact.Email;
                }
            }
        },
        'delivery_address.postal_code': debounce(function debCaOrDeZip(val) {
            if (val) {
                this.checkPostalCode(val, this.delivery_address.country_code);
            }
        }, 500),
        'delivery_address.country_code': debounce(function debCaOrDeCc(val) {
            if (this.delivery_address.postal_code && val) {
                this.checkPostalCode(this.delivery_address.postal_code, val);
            }
        }, 500),
        edit_contact_id(val) {
            if (val !== null && val >= 0) {
                this.$bvModal.show('modal-contact-edit');
            }
        },
        'card_order.special_commission_approved_by': async function watchCardOrderSpecialCommissionApprovedBy(userId) {
            const repository = new UserRepository();
            this.special_commission_approved_by_user = await repository.getById(userId);
        },
        'card_order.DeliveryStatusID': async function watchCardOrderDeliveryStatusID(newId, oldId) {
            let doChange = true;
            if ([300, 400, 450, 500].includes(newId) && newId > oldId && this.card_order.num_cards < this.card_order.num_ordered_cards) {
                doChange = false;
                const newStatus = this.delivery_statuses.find((opt) => opt.id === newId);
                await this.$swal({
                    text: 'Antalet aktiverade kort är färre än beställt. Vill du verkligen ändra status till ' + (newStatus ? newStatus.label : newId) + '?',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Ja, ändra!',
                    cancelButtonText: 'Avbryt',
                }).then((result) => {
                    if (result.isConfirmed) {
                        doChange = true;
                    }
                });
            }
            if (doChange) {
                if ([400, 450, 500].includes(newId) && this.card_order.StatusID < 750) {
                    this.$nextTick(() => {
                        this.$swal({
                            text: 'Vill du även ändra orderstatus till "Klar för leverans"?',
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonText: 'Ja, ändra!',
                            cancelButtonText: 'Avbryt',
                        })
                            .then((result) => {
                                if (result.isConfirmed) {
                                    this.changeOrderStatus(750);
                                }
                            });
                    });
                }
            } else {
                this.card_order.DeliveryStatusID = oldId;
            }
        },
        mail: {
            deep: true,
            handler: debounce(function mailHandler() {
                const vm = this;
                vm.updateMailPreview();
            }),
        },
        customer() {
            this.getCustomerPriceLists();
        },
    },
    methods: {
        nl2br,
        formatCurrency,
        hasPermission,
        genMod10,
        openPopupWindow,
        formatDateTime,
        hasRole,
        async changeOrderStatus(statusId) {
            if (this.confirming_order_status || Number(statusId) === Number(this.card_order.StatusID)) return;
            const hasCustomGreeting = Number(this.card_order.hasCustomPrint) === 1 || Number(this.card_order.hasCustomWeb) === 1;
            if (Number(statusId) === 750 && hasCustomGreeting && (!this.production_files.approved || !this.hasGreetingImage)) {
                this.confirming_order_status = true;
                try {
                    const result = await this.$swal({
                        text: 'Godkänt korr saknas, vill du verkligen sätta ordern till Klar för leverans?',
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: 'Ja, ändra!',
                        cancelButtonText: 'Avbryt',
                    });
                    if (!result.isConfirmed) return;
                } finally {
                    this.confirming_order_status = false;
                    // Reset the selector's local value even when the order status stays unchanged.
                    this.order_status_selector_key += 1;
                }
            }
            this.card_order.StatusID = statusId;
        },
        async getCardOrder(keepCurrentOrderData = false) {
            this.busy_loading = true;
            await apiClient.get('/vue/cardorders/' + this.id + (this.customer_id ? '?customer_id=' + this.customer_id : ''))
                .then((response) => {
                    if (!Array.isArray(response.data.item_flags) && typeof response.data.item_flags === 'object') {
                        Vue.set(this, 'item_flags', response.data.item_flags);
                    }
                    if (!Array.isArray(response.data.item_voucher) && typeof response.data.item_voucher === 'object') {
                        Vue.set(this, 'item_voucher', response.data.item_voucher);
                    }
                    if (!Array.isArray(response.data.item_valid_range) && typeof response.data.item_valid_range === 'object') {
                        Vue.set(this, 'item_valid_range', response.data.item_valid_range);
                    }
                    if (!keepCurrentOrderData) {
                        Vue.set(this, 'card_order', response.data.card_order);
                        this.greeting_image_selected = false;
                    }
                    Vue.set(this, 'customer', response.data.customer);
                    Vue.set(this, 'unifaun_data', response.data.unifaun_data);
                    Vue.set(this, 'ntaps_parcels', response.data.ntaps_parcels);
                    Vue.set(this, 'order_payments', response.data.order_payments);
                    Vue.set(this, 'payment_links', response.data.payment_links);
                    Vue.set(this, 'hide_kolli', []);
                    Vue.set(this, 'mail_messages', response.data.mail_messages);
                    this.saved_order_greeting = greetingValues(response.data.card_order);
                    this.checkContainsEcards();
                    this.loadProductionFiles();
                })
                .catch((error) => {
                    const { status } = error.response;
                    if (status === 404) {
                        Vue.set(this, 'not_found', true);
                    }
                })
                .finally(() => {
                    Vue.set(this, 'busy_loading', false);
                    Vue.set(this, 'initialized', true);
                    Vue.set(this, 'not_found', false);
                });
        },
        async getProductInfo() {
            this.busy_loading_product_info = true;
            await apiClient.get('/vue/cardorders/' + this.id + '/product_info')
                .then((response) => {
                    Vue.set(this, 'product_info', response.data);
                    Vue.set(this, 'show_product_info', true);
                })
                .finally(() => {
                    Vue.set(this, 'busy_loading_product_info', false);
                });
        },
        async getCardInfo() {
            this.busy_loading_card_info = true;
            await apiClient.get('/vue/cardorders/' + this.id + '/card_info')
                .then((response) => {
                    Vue.set(this, 'card_info', response.data);
                    Vue.set(this, 'show_card_info', true);
                })
                .finally(() => {
                    Vue.set(this, 'busy_loading_card_info', false);
                });
        },
        async saveCardOrder() {
            const isValidOrder = await this.$refs.orderform.validate();
            const isValidSeller = await this.$refs.sellerform.validate();
            if (isValidOrder && isValidSeller) {
                let decision;
                try {
                    decision = this.greeting_image_selected ? {} : await approvalChangeDecision(this, this.production_files, greetingValues(this.card_order) !== this.saved_order_greeting);
                } catch (error) {
                    this.$refs.btnsaveorder.triggerFailed(); this.$refs.btnsaveseller.triggerFailed(); this.$refs.btnsaveconnectedorders.triggerFailed();
                    return;
                }
                this.busy_saving = true;
                await apiClient.post('/vue/cardorders/' + this.id, {
                    ...decision,
                    ...(this.greeting_image_selected && this.hasGreetingImage ? { greeting_source: 'manual_image' } : {}),
                    ...(this.greeting_image_selected ? { archive_revision: this.production_files.revision } : {}),
                    hide_kolli: this.hide_kolli,
                    card_order: this.card_order,
                    item_flags: this.item_flags,
                    item_voucher: this.item_voucher,
                }).then((response) => {
                    if (parseInt(this.id, 10) !== parseInt(response.data.card_order.OrderID, 10)) {
                        window.location.href = '/cardorders/' + response.data.card_order.OrderID;
                    }
                    Vue.set(this, 'card_order', response.data.card_order);
                    Vue.set(this, 'customer', response.data.customer);
                    Vue.set(this, 'unifaun_data', response.data.unifaun_data);
                    Vue.set(this, 'ntaps_parcels', response.data.ntaps_parcels);
                    Vue.set(this, 'hide_kolli', []);
                    Vue.set(this, 'unpaid_amount', response.data.unpaid_amount);
                    Vue.set(this, 'mail_messages', response.data.mail_messages);
                    if (!Array.isArray(response.data.item_flags) && typeof response.data.item_flags === 'object') {
                        Vue.set(this, 'item_flags', response.data.item_flags);
                    }
                    if (!Array.isArray(response.data.item_voucher) && typeof response.data.item_voucher === 'object') {
                        Vue.set(this, 'item_voucher', response.data.item_voucher);
                    }
                    if (!Array.isArray(response.data.item_valid_range) && typeof response.data.item_valid_range === 'object') {
                        Vue.set(this, 'item_valid_range', response.data.item_valid_range);
                    }
                    this.saved_order_greeting = greetingValues(response.data.card_order);
                    this.greeting_image_selected = false;
                    this.loadProductionFiles();
                    this.checkContainsEcards();
                    this.$refs.btnsaveorder.triggerSaved();
                    this.$refs.btnsaveseller.triggerSaved();
                    this.$refs.btnsaveconnectedorders.triggerSaved();
                }).catch((error) => {
                    const parsederror = getError(error);
                    if (typeof parsederror === 'object') {
                        this.$refs.orderform.setErrors(parsederror);
                        this.$refs.sellerform.setErrors(parsederror);
                        this.$refs.connectedordersform.setErrors(parsederror);
                    }
                    this.$refs.btnsaveorder.triggerFailed();
                    this.$refs.btnsaveseller.triggerFailed();
                    this.$refs.btnsaveconnectedorders.triggerFailed();
                }).finally(() => {
                    this.busy_saving = false;
                });
            } else {
                this.$refs.btnsaveorder.triggerFailed();
                this.$refs.btnsaveseller.triggerFailed();
                this.$refs.btnsaveconnectedorders.triggerFailed();
            }
        },
        async copyCardOrder() {
            let doCopy = false;
            await this.$swal({
                text: 'Är du säker att du skapa en kopia på denna order?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Ja, kopiera!',
                cancelButtonText: 'Avbryt',
            }).then((result) => {
                if (result.isConfirmed) {
                    doCopy = true;
                }
            });
            if (doCopy) {
                this.busy_saving = true;
                await apiClient.post('/vue/cardorders/' + this.id + '/copy')
                    .then((response) => {
                        if (parseInt(this.id, 10) !== parseInt(response.data.card_order.OrderID, 10)) {
                            window.location.href = '/cardorders/' + response.data.card_order.OrderID;
                        }
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.orderform.setErrors(parsederror);
                        }
                        this.$refs.btncopyorder.triggerFailed();
                    }).finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        getCustomerPriceLists() {
            this.busy_loading_price_lists = true;
            apiClient.get('/vue/customers/' + this.customer.CustomerID + '/price_lists')
                .then((response) => {
                    this.price_lists = response.data;
                })
                .finally(() => {
                    this.busy_loading_price_lists = false;
                });
        },
        getDeliveryAddresses() {
            this.busy_loading_addresses = true;
            apiClient.get('/vue/cardorders/' + this.id + '/delivery_addresses')
                .then((response) => {
                    this.delivery_addresses = response.data;
                })
                .finally(() => {
                    this.busy_loading_addresses = false;
                });
        },
        showModalEditAddress(address = null) {
            if (address) {
                this.delivery_address = address;
            } else {
                this.delivery_address = {
                    id: 0,
                    name: '',
                    co: '',
                    street_1: '',
                    street_2: '',
                    postal_code: '',
                    city: '',
                    region: '',
                    country_code: 'SE',
                    contact_name: '',
                    contact_phone: '',
                    contact_email: '',
                    service_type: null,
                    items: [],
                };
            }
            this.$bvModal.show('edit_delivery_address');
        },
        async saveDeliveryAddress(e) {
            e.preventDefault();
            const isValid = await this.$refs.deliveryaddressform.validate();
            if (isValid) {
                this.busy_saving_address = true;
                await apiClient.post('/vue/cardorders/' + this.id + '/delivery_addresses/' + this.delivery_address.id, { ...this.delivery_address })
                    .then(() => {
                        this.getDeliveryAddresses();
                        this.$bvModal.hide('edit_delivery_address');
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.deliveryaddressform.setErrors(parsederror);
                        }
                        this.$refs.btnsavedeliveryaddress.triggerFailed();
                    }).finally(() => {
                        this.busy_saving_address = false;
                    });
            } else {
                this.$refs.btnsavedeliveryaddress.triggerFailed();
            }
        },
        async deleteDeliveryAddress(addr) {
            this.busy_deleting_address = true;
            await apiClient.delete('/vue/cardorders/' + this.id + '/delivery_addresses/' + addr.id)
                .then(() => {
                    this.getDeliveryAddresses();
                    this.$bvModal.hide('edit_delivery_address');
                }).finally(() => {
                    this.busy_deleting_address = false;
                });
        },
        init() {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const customerId = urlParams.get('customer_id');
            if (customerId) {
                this.customer_id = customerId;
            }
            this.getCardOrder();
            if (this.id > 0) {
                this.getDeliveryAddresses();
            }
        },
        addOrderItem() {
            this.card_order.items.push({
                OrderItemID: 0,
                CardSeriesID: '',
                CardOrderLine: '',
                CardAmount: '',
                CardPrice: '',
                CardFlags: '',
                no_of_points: 0,
                SeriesName: '',
                ProdName: '',
                IsPoints: 0,
            });
        },
        checkPostalCode(code, country = 'SE') {
            if (code && country) {
                apiClient.get('/vue/delivery/postalcode?postal_code=' + code + '&country_code=' + country)
                    .then((response) => {
                        this.delivery_info = (response.data.Comment ? response.data.Comment : 'NORMAL') + ', ' + response.data.Days + ' dagar';
                    })
                    .catch(() => {
                        if (country !== 'SE') {
                            this.delivery_info = 'UTLAND Lev.tid saknas';
                        } else {
                            this.delivery_info = 'Postnummer hittades inte';
                        }
                    });
            } else {
                this.delivery_info = '';
            }
        },
        showModalStockLabel(orderID, addressId = null) {
            const printerTag = Cookies.get('small_label_printer');
            if (printerTag) {
                this.small_label.printer_tag = printerTag;
            }
            this.small_label.template = 'adresslapp_utan_frankering';
            this.small_label.card_order_id = orderID;
            this.small_label.card_order_address_id = addressId;
            this.$root.$bvModal.show('modal_print_small_label');
        },
        printSmallLabelPDF(addressId) {
            const addr = this.delivery_addresses.find((a) => a.id === addressId);
            if (addr) {
                let url = this.old_system_url + '/showPDF_PtouchLabel.php';
                url += '?txt_name=' + (addr.name ? encodeURIComponent(addr.name) : '');
                url += '&txt_co=' + (addr.co ? encodeURIComponent(addr.co) : '');
                url += '&txt_address1=' + (addr.street_1 ? encodeURIComponent(addr.street_1) : '');
                url += '&txt_address2=' + (addr.street_2 ? encodeURIComponent(addr.street_2) : '');
                url += '&txt_zipcode=' + (addr.postal_code || addr.city ? encodeURIComponent(addr.postal_code + ' ' + addr.city) : '');
                this.openPopupWindow(url, 'PtouchLabelWin', 500, 400);
                this.$root.$bvModal.hide('modal_print_small_label');
            }
        },
        async printSmallLabel(event) {
            event.preventDefault();
            const isValid = await this.$refs.printsmalllabelform.validate();
            if (isValid) {
                Cookies.set('small_label_printer', this.small_label.printer_tag, { expires: 365 });
                this.busy_printing_small_label = true;
                await apiClient.post('/api/printer/print/address/' + this.small_label.printer_tag + '/' + this.small_label.template, this.small_label).then(() => {
                    this.$root.$bvModal.hide('modal_print_small_label');
                }).finally(() => {
                    this.busy_printing_small_label = false;
                });
            } else {
                this.$refs.btnprintsmalllabel.triggerFailed();
            }
        },
        searchProducts: debounce(async function searchProducts(search) {
            const vm = this;
            await apiClient.get('/vue/products', { params: { search, per_page: 8 } })
                .then((response) => {
                    vm.select_products = response.data.data;
                });
        }, 300),
        fillDeliveryAddress(address) {
            if (this.delivery_address && address) {
                this.delivery_address.name = address.DeliveryCompany;
                this.delivery_address.street_1 = address.DeliveryAddress1;
                this.delivery_address.street_2 = address.DeliveryAddress2;
                this.delivery_address.postal_code = address.DeliveryZip;
                this.delivery_address.city = address.DeliveryCity;
                this.delivery_address.country_code = address.DeliveryCountryCode;
                this.delivery_address.contact_name = address.DeliveryContactName;
                this.delivery_address.contact_phone = address.DeliveryContactPhone;
                this.delivery_address.contact_email = address.DeliveryContactEmail;
            }
        },
        async selectContact(contact = null) {
            await this.getCardOrder(true);
            this.card_order.ContactID = null;
            this.$bvModal.hide('modal-contact-edit');
            if (contact) {
                this.$nextTick(() => {
                    this.card_order.ContactID = contact.ContactID;
                });
            }
        },
        checkContainsEcards() {
            this.show_ecard_list_button = false;
            if (this.card_order && this.card_order.items && Array.isArray(this.card_order.items)) {
                this.card_order.items.forEach((item) => {
                    if (item.serie && item.serie.card_serie_type && item.serie.card_serie_type.CardType.endsWith('_ecard')) {
                        this.show_ecard_list_button = true;
                    }
                });
            }
        },
        async showCodes(item) {
            await apiClient.get('/vue/cardorders/items/' + item.OrderItemID + '/codes?claim=1')
                .then((response) => {
                    item.codes = [...response.data];
                    this.codes_item = item;
                });
            this.$bvModal.show('modal-show-codes');
        },
        showModalEditDate() {
            this.$bvModal.show('modal-edit-date');
        },
        showModalRegisterPayment() {
            const date = new Date();
            this.payment = {
                order_id: this.card_order.OrderID,
                amount: '',
                comment: '',
                reference: '',
                payment_date: date.toISOString().substring(0, 10),
                source: 'manual_payment',
            };
            this.$bvModal.show('modal-register-payment');
        },
        async registerPayment(e) {
            e.preventDefault();
            const isValid = await this.$refs.paymentform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/cardorders/' + this.card_order.OrderID + '/payments/0', { ...this.payment })
                    .then((response) => {
                        this.order_payments.push(response.data);
                        this.$refs.btnregisterpayment.triggerSaved();
                        this.$bvModal.hide('modal-register-payment');
                    }).catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.paymentform.setErrors(parsederror);
                        }
                        this.$refs.btnregisterpayment.triggerFailed();
                    }).finally(() => {
                        this.busy_saving = false;
                    });
            } else {
                this.$refs.btnregisterpayment.triggerFailed();
            }
        },
        createPaymentLink() {
            this.busy_create_paylink = true;
            apiClient.post('/vue/cardorders/' + this.card_order.OrderID + '/payment_links/create')
                .then((response) => {
                    this.payment_links.push(response.data);
                    this.$refs.btncreatepaylink.triggerSaved();
                }).catch((error) => {
                    const parsederror = getError(error);
                    if (typeof parsederror === 'object') {
                        this.$refs.paymentform.setErrors(parsederror);
                    }
                    this.$refs.btncreatepaylink.triggerFailed();
                }).finally(() => {
                    this.busy_create_paylink = false;
                });
        },
        updateMailPreview() {
            this.mail_preview_url = '/cardorders/' + this.id + '/mailpreview/' + this.mail_type + '?' + Object.keys(this.mail).filter((k) => this.mail[k]).map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(this.mail[k])}`).join('&');
            this.pdf_preview_url = '';
            if (this.mail.pdf) {
                this.pdf_preview_url = '/cardorders/' + this.id + '/pdf/' + this.mail.pdf;
            }
        },
        showModalMail(mailType) {
            if (this.busy_sending_mail) return;
            this.mail_type = mailType;
            this.updateMailPreview();
            this.$root.$bvModal.show('modal_mail_preview');
            if (this.card_order.CustomerContactMail) {
                this.mail.to = this.card_order.CustomerContactMail;
            }
            if (['confirmation', 'quote', 'proforma'].includes(mailType)) {
                this.mail.pdf = this.mail_type;
            } else {
                this.mail.pdf = null;
            }
            this.mail.text_address_list = false;
            this.mail_attachments = [];
            if (this.$refs.mailattachmentinput) {
                this.$refs.mailattachmentinput.clear();
            }
            if (this.mail_messages[mailType] && this.mail_messages[mailType].standard_message) {
                this.mail.message = this.mail_messages[mailType].standard_message.replaceAll('\\n', '\n');
                // if (mailType === 'delivery') {
                //     const kolli = this.unifaun_data.filter((row) => !row.isHidden && row.kollino);
                //     if (kolli.length > 0) {
                //         kolli.forEach((row) => {
                //             this.mail.message += '<a href="https://www.postnord.se/vara-verktyg/spara-brev-paket-och-pall?shipmentId=' + row.kollino + '" target="_blank">' + row.kollino + '</a>';
                //             const toArr = [row.name, row.address1, row.address2, row.zipcode, row.city].filter((txt) => txt);
                //             if (toArr.length > 0) {
                //                 this.mail.message += ', ' + toArr.join(', ');
                //             }
                //             this.mail.message += '\n';
                //         });
                //     } else {
                //         this.mail.message = '';
                //     }
                // }
            } else {
                this.mail.message = '';
            }
        },
        mailAttachmentsChanged(files) {
            this.mail_attachments = Array.from(files || []);
            this.$refs.mailform.setErrors({ attachments: this.mailAttachmentsTooLarge ? ['Bilagorna får vara max 8 MB totalt.'] : [] });
        },
        async sendMail(event) {
            event.preventDefault();
            if (this.busy_sending_mail) return;
            this.busy_sending_mail = true;
            if (this.mailAttachmentsTooLarge) {
                this.$refs.btnsendmail.triggerFailed();
                this.$refs.mailform.setErrors({ attachments: ['Bilagorna får vara max 8 MB totalt.'] });
                return;
            }
            const formData = new FormData();
            Object.keys(this.mail).forEach((key) => {
                const value = this.mail[key];
                if (value === null || value === undefined) {
                    return;
                }
                if (typeof value === 'boolean') {
                    formData.append(key, value ? 1 : 0);
                } else {
                    formData.append(key, value);
                }
            });
            this.mail_attachments.forEach((file) => formData.append('attachments[]', file));
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            };
            await apiClient.post('/vue/cardorder/' + this.card_order.OrderID + '/send/' + this.mail_type, formData, config)
                .then(() => {
                    const message = this.mail_type_human.charAt(0).toUpperCase() + this.mail_type_human.slice(1) + ' skickad!';
                    this.$root.$bvModal.hide('modal_mail_preview');
                    this.$swal({
                        icon: 'success',
                        title: message,
                        confirmButtonText: 'OK',
                        showCancelButton: false,
                        timer: 2222,
                    });
                }).catch((error) => {
                    this.$refs.btnsendmail.triggerFailed();
                    const parsederror = getError(error);
                    if (typeof parsederror === 'object') {
                        Object.keys(parsederror).filter((key) => key.startsWith('attachments.')).forEach((key) => {
                            parsederror.attachments = [...(parsederror.attachments || []), ...parsederror[key]];
                        });
                        this.$refs.mailform.setErrors(parsederror);
                    } else {
                        let message = 'Något gick fel...';
                        if (error.response && error.response.data && error.response.data.message) {
                            message = error.response.data.message;
                        }
                        this.$swal({
                            icon: 'error',
                            title: 'Error!',
                            text: message,
                            confirmButtonText: 'OK',
                            cancelButtonText: 'Avbryt',
                        });
                    }
                }).finally(() => {
                    this.busy_sending_mail = false;
                });
        },
        inputItemSerie(serie, itemIndex) {
            if (serie) {
                const cardType = serie.card_serie_type ? serie.card_serie_type.CardType : null;
                const priceList = this.customer && Array.isArray(this.customer.customer_price_lists)
                    ? this.customer.customer_price_lists.find((item) => parseInt(item.CardSeriesID, 10) === parseInt(serie.CardSeriesID, 10))
                    : null;
                const priceListPrice = priceList ? parseFloat(priceList.Price_SEK) : NaN;
                const baseDiscount = this.customer && this.customer.baseDiscount && cardType
                    ? parseFloat(this.customer.baseDiscount[cardType])
                    : NaN;
                const groupDiscount = this.customer && this.customer.customer_group_sht
                    ? parseFloat(this.customer.customer_group_sht.groupDiscount)
                    : 0;
                const discount = Number.isNaN(baseDiscount) ? groupDiscount : baseDiscount;
                const giftFreightPrice = parseInt(this.customer.CustomerGroupID_SHT, 10) === 102
                    ? parseFloat(serie.GiftFreightPrice)
                    : 0;
                const cardPrice = parseFloat(serie.CardPrice);
                const itemPrice = !Number.isNaN(priceListPrice) ? priceListPrice : cardPrice;
                const discountedPrice = Number.isNaN(priceListPrice) && discount > 0 && !Number.isNaN(itemPrice)
                    ? itemPrice * ((100 - discount) / 100)
                    : itemPrice;

                this.card_order.items[itemIndex].CardSeriesID = serie.CardSeriesID;
                this.card_order.items[itemIndex].CardOrderLine = serie.SeriesName;
                this.card_order.items[itemIndex].CardPrice = !Number.isNaN(discountedPrice)
                    ? discountedPrice + (Number.isNaN(giftFreightPrice) ? 0 : giftFreightPrice)
                    : serie.CardPrice;
                if (!this.item_voucher[serie.CardSeriesID]) {
                    this.item_voucher[serie.CardSeriesID] = {
                        voucher_type: serie.voucher_type,
                        voucher_vat: serie.VAT,
                    };
                }
                this.card_order.items[itemIndex].VAT = this.item_voucher[serie.CardSeriesID].voucher_vat;
            } else {
                this.card_order.items[itemIndex].CardSeriesID = '';
                this.card_order.items[itemIndex].CardOrderLine = '';
            }
        },
        inputItemProd(prod, itemIndex) {
            if (prod) {
                this.card_order.items[itemIndex].CardSeriesID = 'P' + prod.ProdID;
                this.card_order.items[itemIndex].CardPrice = prod.ProdDirectSalesPrice;
                if (!this.item_voucher['P' + prod.ProdID]) {
                    this.item_voucher['P' + prod.ProdID] = {
                        voucher_type: 0,
                        voucher_vat: prod.ProdVAT,
                    };
                }
                this.card_order.items[itemIndex].VAT = this.item_voucher['P' + prod.ProdID].voucher_vat;
            } else {
                this.card_order.items[itemIndex].CardSeriesID = 'P';
                this.card_order.items[itemIndex].CardPrice = '';
            }
        },
        updateItemsVat(serie, vat) {
            this.card_order.items.forEach((item) => {
                if (item.CardSeriesID === serie) {
                    // eslint-disable-next-line no-param-reassign
                    item.VAT = vat;
                }
            });
        },
        inputFileChanged(files) {
            this.address_list_files = files;
        },
        showUploadAddressList() {
            this.$bvModal.show('modal_upload_address_list');
        },
        async uploadAddressList(e) {
            e.preventDefault();
            if (this.address_list_files && this.address_list_files[0]) {
                this.busy_uploading_addresses = true;
                const formData = new FormData();
                formData.append('file', this.address_list_files[0]);
                formData.append('replace', this.address_list_replace);
                const config = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                };
                await apiClient.post('/vue/cardorders/' + this.id + '/delivery_addresses/upload', formData, config)
                    .then(() => {
                        this.getDeliveryAddresses();
                        this.$refs.addresslistfileinput.clear();
                        this.address_list_files = null;
                        this.address_list_replace = false;
                        this.$bvModal.hide('modal_upload_address_list');
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.addresslistfileinput.clear();
                            this.address_list_files = null;
                            this.$refs.uploadaddresslistform.setErrors(parsederror);
                        }
                        this.$refs.btnuploadaddresslist.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_uploading_addresses = false;
                    });
            } else {
                this.$refs.btnuploadaddresslist.triggerFailed();
            }
        },
        getPrinters() {
            const client = new PrinterRepository();
            client.whereIn('media', ['adr_101x38', 'adr_99x49'])
                .where('language', 'zpl')
                .orderBy('name')
                .orderDirection('ASC')
                .setPerPage(999999);

            if (!this.hasRole('super-admin')) {
                client.where('is_dev', 0);
            }

            client.run().then((response) => {
                this.smalllabel_printers = response.data;
            });
        },
        checkTryCredit: debounce(function checkTryCredit(event) {
            if (event.target.value < 0 && !this.credit_warning_shown) {
                this.credit_warning_shown = true;
                this.$swal({
                    title: 'Försöker du skapa en kredit?',
                    text: 'Gör istället (del)kredit av den ursprungliga fakturan. Gå in på fakturan, klicka "skapa kredit", välj antal som ska krediteras, klicka "skapa".',
                    icon: 'warning',
                    showCancelButton: false,
                    confirmButtonText: 'OK!',
                });
            }
        }, 300),
        updateItemSeries(event, itemIdx) {
            const newSeries = event.target.value;
            const oldSeries = this.card_order.items[itemIdx].CardSeriesID;

            const normalizedSeries = String(newSeries).toLowerCase();
            const prod = this.select_products.find((item) => ('p' + item.ProdID).toLowerCase() === normalizedSeries);
            const serie = this.card_series.find((item) => String(item.CardSeriesID).toLowerCase() === normalizedSeries);

            if (prod) {
                this.inputItemProd(prod, itemIdx);
            } else if (serie) {
                this.inputItemSerie(serie, itemIdx);
            } else {
                this.card_order.items[itemIdx].CardSeriesID = newSeries;
            }

            const itemSeries = this.card_order.items[itemIdx].CardSeriesID;
            if (!Object.hasOwn(this.item_flags, itemSeries) && Object.hasOwn(this.item_flags, oldSeries)) {
                this.item_flags[itemSeries] = [...this.item_flags[oldSeries]];
            }
            if (!Object.hasOwn(this.item_voucher, itemSeries) && Object.hasOwn(this.item_voucher, oldSeries)) {
                this.item_voucher[itemSeries] = { ...this.item_voucher[oldSeries] };
            }
        },
        async showModalCreatePrint(tab = 'print') {
            this.production_dialog_tab = tab;
            await this.loadProductionFiles();
            this.$bvModal.show('modal_create_print');
        },
        async loadProductionFiles() {
            if (!this.card_order?.OrderID) return;
            const response = await apiClient.get(`/vue/cardorders/${this.id}/production`);
            this.production_files = response.data;
        },
        productionFilesUpdated(state) {
            const approvalChanged = this.production_files.approved !== state.approved;
            this.production_files = state;
            if (this.card_order) this.card_order.korrAccepted = state.approved ? 1 : 0;
            if (approvalChanged && state.order_greeting && this.card_order) {
                greetingFields.forEach((key) => { Vue.set(this.card_order, key, state.order_greeting[key]); });
                this.saved_order_greeting = greetingValues(this.card_order);
            }
            if (this.$refs.orderFiles) this.$refs.orderFiles.getFiles();
        },
        greetingImageSelected() { this.greeting_image_selected = true; },
        async showModalCreateKorr() {
            if (this.greeting_image_selected || (this.production_files.proof_source === 'manual_image' && greetingValues(this.card_order) !== this.saved_order_greeting)) {
                await this.$swal({ text: 'Spara den valda hälsningsbilden på ordern innan du skapar korr.', icon: 'info', confirmButtonText: 'OK' });
                return;
            }
            this.$bvModal.show('modal_create_korr');
        },

    },
    computed: {
        hasGreetingImage() { return Number(this.card_order?.GreetingImageID) > 0 && Number.isInteger(Number(this.card_order.GreetingImageID)); },
        manualGreetingImage() { return this.hasGreetingImage && (this.greeting_image_selected || this.production_files.proof_source === 'manual_image'); },
        productionStatus() {
            const changed = this.greeting_image_selected || greetingValues(this.card_order) !== this.saved_order_greeting;
            return {
                ...this.production_files,
                unsaved_source_selection: this.greeting_image_selected,
                changed_since_approval: this.production_files.changed_since_approval || (!!this.production_files.approved && changed),
            };
        },
        barcode() {
            if (this.card_order) {
                return [...this.card_order.OrderID.toString()];
            }
            return [];
        },
        select_statuses() {
            const arr = [];
            Object.values(this.statuses).forEach((status) => {
                if (status.id === 0 || status.id === 500 || status.id === 900) {
                    arr.push({ id: status.id, label: status.name.toUpperCase() });
                }
            });
            arr.push({ id: '-----', label: '-----', disabled: true });
            Object.values(this.statuses).forEach((status) => {
                if (![0, 400, 500, 505, 900].includes(status.id)) {
                    arr.push({ id: status.id, label: status.name });
                }
            });
            return arr;
        },
        select_invoice_addresses() {
            const arr = [];
            if (this.card_order && this.customer.invoice_addresses) {
                Object.values(this.customer.invoice_addresses).forEach((address) => {
                    const textarr = [];
                    if (address.DeliveryCompany) {
                        textarr.push(address.DeliveryCompany);
                    }
                    if (address.DeliveryAddress1) {
                        textarr.push(address.DeliveryAddress1);
                    }
                    if (address.DeliveryAddress2) {
                        textarr.push(address.DeliveryAddress2);
                    }
                    if (address.DeliveryCity) {
                        textarr.push(address.DeliveryCity);
                    }
                    if (address.DeliveryCountryCode) {
                        textarr.push(address.DeliveryCountryCode);
                    }
                    if (address.DeliveryContactName) {
                        textarr.push(address.DeliveryContactName);
                    }
                    arr.push({ id: address.DelivAdrID, label: textarr.join(', ') });
                });
            }
            return arr;
        },
        select_delivery_addresses() {
            const arr = [];
            if (this.customer && this.customer.delivery_addresses) {
                Object.values(this.customer.delivery_addresses).forEach((address) => {
                    const textarr = [];
                    if (address.DeliveryCompany) {
                        textarr.push(address.DeliveryCompany);
                    }
                    if (address.DeliveryAddress1) {
                        textarr.push(address.DeliveryAddress1);
                    }
                    if (address.DeliveryAddress2) {
                        textarr.push(address.DeliveryAddress2);
                    }
                    if (address.DeliveryCity) {
                        textarr.push(address.DeliveryCity);
                    }
                    if (address.DeliveryCountryCode) {
                        textarr.push(address.DeliveryCountryCode);
                    }
                    if (address.DeliveryContactName) {
                        textarr.push(address.DeliveryContactName);
                    }
                    arr.push({ id: address.DelivAdrID, label: textarr.join(', '), ...address });
                });
            }
            return arr;
        },
        select_fonts() {
            return [
                { id: 'Font-Family: Arial; Font-Size: 14px; Font-Weight: Bold; color: #990000;', label: 'Normal - Arial' },
                { id: 'Font-Family: Arial; Font-Size: 24px; Font-Weight: Bold; color: #990000;', label: 'Stor - Arial' },
                { id: 'Font-Family: Times New Roman; Font-Size: 14px; Font-Weight: Bold; color: #990000;', label: 'Normal - Times New Roman' },
                { id: 'Font-Family: Times New Roman; Font-Size: 24px; Font-Weight: Bold; color: #990000;', label: 'Stor - Times New Roman' },
                { id: 'Font-Family: Verdana; Font-Size: 14px; Font-Weight: Bold; color: #990000;', label: 'Normal - Verdana' },
                { id: 'Font-Family: Verdana; Font-Size: 24px; Font-Weight: Bold; color: #990000;', label: 'Stor - Verdana' },
                { id: 'Font-Family: Comic Sans MS; Font-Size: 14px; Font-Weight: Bold; color: #990000;', label: 'Normal - Comic Sans MS' },
                { id: 'Font-Family: Comic Sans MS; Font-Size: 24px; Font-Weight: Bold; color: #990000;', label: 'Stor - Comic Sans MS' },
                { id: 'Font-Family: Impact; Font-Size: 14px; Font-Weight: Bold; color: #990000;', label: 'Normal - Impact' },
                { id: 'Font-Family: Impact; Font-Size: 24px; Font-Weight: Bold; color: #990000;', label: 'Stor - Impact' },
                { id: 'Font-Family: Avant Garde; Font-Size: 14px; Font-Weight: Bold; color: #990000;', label: 'Normal - Avant Garde' },
                { id: 'Font-Family: Avant Garde; Font-Size: 24px; Font-Weight: Bold; color: #990000;', label: 'Stor - Avant Garde' },
            ];
        },
        select_contacts() {
            const arr = [];
            if (this.card_order && this.customer.contacts) {
                Object.values(this.customer.contacts).forEach((contact) => {
                    const textarr = [];
                    if (contact.FullName) {
                        textarr.push(contact.FullName);
                    }
                    if (contact.Email) {
                        textarr.push(contact.Email);
                    }
                    if (contact.PhoneLandline) {
                        textarr.push(contact.PhoneLandline);
                    }
                    if (contact.PhoneMobile) {
                        textarr.push(contact.PhoneMobile);
                    }
                    arr.push({ id: contact.ContactID, label: textarr.join(', ') });
                });
            }
            return arr;
        },
        send_date_str() {
            if (this.card_order && this.card_order.CalcShippingDate) {
                const d = new Date(this.card_order.CalcShippingDate);
                if (d) {
                    return d.toLocaleString('sv', { weekday: 'long' });
                }
            }
            return '';
        },
        deliv_date_str() {
            if (this.card_order && this.card_order.DeliveryDate) {
                const d = new Date(this.card_order.DeliveryDate);
                if (d) {
                    if (this.card_order.CreatedTS) {
                        const deliveryDateString = this.card_order.DeliveryDate.substring(0, 10);
                        const createdDateString = this.card_order.CreatedTS.substring(0, 10);
                        const createdDate = new Date(createdDateString);
                        createdDate.setDate(createdDate.getDate() + 1);
                        const nextDayDateString = createdDate.toISOString().substring(0, 10);

                        if ([createdDateString, nextDayDateString].includes(deliveryDateString)) {
                            return 'ASAP';
                        }
                    }
                    if (d.getDay() === 0 || d.getDay() === 6) {
                        return 'Vecka ' + getWeek(this.card_order.DeliveryDate);
                    }
                    return d.toLocaleString('sv', { weekday: 'long' });
                }
            }
            return '';
        },
        internal_note() {
            if (this.card_order && this.customer && this.customer.InternalNote) {
                const n = this.customer.InternalNote.indexOf('***');
                return (this.customer.InternalNote.substring(0, n !== -1 ? n : this.customer.InternalNote.length)).replaceAll('\n', '<br>');
            }
            return '';
        },
        num_cards_text() {
            let html = '';
            if (this.card_order.OrderID > 0) {
                html += '<div>' + this.card_order.num_cards + ' st inlästa kort';
                if (this.card_order.num_cards_activated > 0) {
                    html += ', varav ' + this.card_order.num_cards_activated + ' st är aktiverade.';
                } else {
                    html += '.';
                }
                if (this.card_order.num_cards_redeemed > 0 || this.card_order.num_cards_blocked > 0) {
                    html += ' Av dessa är ';
                    if (this.card_order.num_cards_redeemed > 0) {
                        html += this.card_order.num_cards_redeemed + ' st inlösta';
                    }
                    if (this.card_order.num_cards_redeemed > 0 && this.card_order.num_cards_blocked > 0) {
                        html += ' samt ';
                    }
                    if (this.card_order.num_cards_blocked > 0) {
                        html += this.card_order.num_cards_blocked + ' st spärrade';
                    }
                    html += '.';
                }
                html += '</div>';
                if (this.card_order.num_cards_activated > 0) {
                    html += '<div class="font-weight-semibold">Totalt ';
                    html += (this.card_order.num_cards_activated - this.card_order.num_cards_blocked);
                    html += ' st aktiva kort.</div>';
                }
            }
            return html;
        },
        branding_logo_url() {
            let url = '';
            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < this.brands.length; i++) {
                if (this.brands[i].tag === this.card_order.Branding && this.brands[i].logo_url) {
                    url = this.brands[i].logo_url;
                    break;
                }
            }
            return url;
        },
        card_order_log() {
            if (!this.show_all_logs && this.card_order && this.card_order.log.length > 0) {
                return [this.card_order.log[0]];
            }
            return this.card_order.log;
        },
        old_system_url() {
            return window.config.app.old_system_url;
        },
        old_gray_system_url() {
            return window.config.app.old_gray_system_url;
        },
        order_total() {
            let sum = 0;
            if (this.card_order && this.card_order.items && Array.isArray(this.card_order.items)) {
                this.card_order.items.forEach((item) => {
                    sum += item.CardAmount * item.CardPrice;
                });
            }
            return sum;
        },
        order_total_inc_vat() {
            let sum = 0;
            if (this.card_order && this.card_order.items && Array.isArray(this.card_order.items)) {
                this.card_order.items.forEach((item) => {
                    let vat = 0;
                    if (this.item_voucher[item.CardSeriesID] && this.item_voucher[item.CardSeriesID].voucher_type === 1) {
                        vat = this.item_voucher[item.CardSeriesID].voucher_vat;
                    } else {
                        vat = item.VAT;
                    }
                    sum += (item.CardAmount * item.CardPrice) * ((100 + vat) / 100);
                });
            }
            return sum;
        },
        order_payments_total_paid() {
            let total = 0;
            if (Array.isArray(this.order_payments)) {
                this.order_payments.forEach((payment) => {
                    if (payment.source !== 'manual_invoice' && payment.source !== 'adyen_invoice') {
                        total += parseFloat(payment.amount);
                    }
                });
            }
            return total;
        },
        order_payments_total_captured_invoices() {
            let total = 0;
            if (Array.isArray(this.order_payments)) {
                this.order_payments.forEach((payment) => {
                    if (payment.source === 'manual_invoice' || payment.source === 'adyen_invoice') {
                        total -= parseFloat(payment.amount);
                    }
                });
            }
            return total;
        },
        invoices_total_paid() {
            let sum = 0;
            if (this.card_order && this.card_order.invoices && Array.isArray(this.card_order.invoices)) {
                this.card_order.invoices.forEach((invoice) => {
                    if (invoice.PaidAmount) {
                        sum += parseFloat(invoice.PaidAmount);
                    }
                });
            }
            return sum;
        },
        left_to_pay() {
            return Math.round(this.order_total_inc_vat) - this.order_payments_total_paid + this.order_payments_total_captured_invoices - this.invoices_total_paid;
        },
        mailAttachmentsTooLarge() {
            return this.mail_attachments.reduce((sum, file) => sum + file.size, 0) > 8 * 1024 * 1024;
        },
        mail_type_human() {
            if (this.mail_type === 'message') {
                return 'meddelande';
            }
            if (this.mail_type === 'confirmation') {
                return 'bekräftelse';
            }
            if (this.mail_type === 'proforma') {
                return 'proforma';
            }
            if (this.mail_type === 'quote') {
                return 'offert';
            }
            if (this.mail_type === 'delivery') {
                return 'delivery';
            }
            return 'mail';
        },
    },
};
</script>
